export interface SampleDataPoint {
  label: string;
  value: string;
  bullets?: string[];
  codeSection?: string;
  source?: string;
}

export interface SampleSection {
  id: string;
  number: string;
  title: string;
  icon: "layers" | "book" | "clipboard" | "plug" | "landmark" | "ruler" | "badge" | "search";
  dataPoints: SampleDataPoint[];
}

export interface SampleReport {
  slug: string;
  reportId: string;
  kicker: string;
  title: string;
  prospect: string;
  prospectUrl: string;
  jurisdiction: string;
  county?: string;
  city?: string;
  projectType: string;
  systemSize: string;
  utility: string;
  reportRun: string;
  verifiedLabel: string;
  sections: SampleSection[];
  footnotes: string[];
}

export const CATALYZE_REPORT: SampleReport = {
  slug: "catalyze",
  reportId: "CR-10D532E8",
  kicker: "Comprehensive Report",
  title: "Dallas, TX — 1MW Roof Mounted Solar + BESS",
  prospect: "Catalyze",
  prospectUrl: "https://catalyze.com/",
  jurisdiction: "Dallas, TX",
  county: "Dallas County",
  city: "Dallas",
  projectType: "C&I",
  systemSize: "1MW",
  utility: "Oncor",
  reportRun: "Jul 28, 2026",
  verifiedLabel: "Jul 2026 · Seal of Authenticity",
  sections: [
    {
      id: "zoning",
      number: "01",
      title: "Zoning & Land Use",
      icon: "layers",
      dataPoints: [
        {
          label: "Non-Residential BESS Setback from Non-Participating Property Line",
          value: "10 feet",
          codeSection: "Section 1207.8.3",
          source: "Dallas Energy Storage System regulations, Section 1207.8.3",
        },
        {
          label: "Non-Residential BESS Setback from Non-Participating Occupied Structure",
          value: "10 feet",
          codeSection: "Sections 51A-4.402(c)(5) and 51A-4.410(c)(6)",
          source: "Dallas Development Code, Sections 51A-4.402(c)(5) and 51A-4.410(c)(6)",
        },
        {
          label: "Solar Property Line Requirements",
          value: "",
          bullets: [
            "Buffers: Clear, brush-free 10-foot perimeter around photovoltaic arrays with approved surface",
          ],
          codeSection: "Sections 1205.5.1 and 1207.8.3",
          source: "International Fire Code, Sections 1205.5.1 and 1207.8.3; Dallas Fire Code 2021, Section 1207.8.3",
        },
        {
          label: "Solar Property Line Setback Distance",
          value: "10 feet",
          codeSection: "Section 1207.8.3; Side Setbacks; Section 1207.10.7.3",
          source:
            "Dallas Fire Code 2021, Section 1207.8.3; General Commercial Zoning Standards; International Fire Code, Section 1207.10.7.3",
        },
        {
          label: "Solar Property Line Variances",
          value: "Yes",
          codeSection: "Section 9",
          source: "County Land Use Regulations, Section 9 Variance Procedure",
        },
      ],
    },
    {
      id: "code-cycle",
      number: "02",
      title: "Code Cycle",
      icon: "book",
      dataPoints: [
        {
          label: "Building Code Edition",
          value: "2021 International Building Code",
          codeSection: "IBC-21",
          source: "ICC International Building Code, IBC-21",
        },
        {
          label: "Electrical Code Edition",
          value: "2020 National Electrical Code",
          codeSection: "Dallas Electrical Code 2021, referencing NFPA 70",
          source:
            "Excerpt references to NFPA 70 and Dallas Electrical Code enforcement under 90.4 and working space requirements per NFPA 70 Section 110.26 as per Dallas Electrical Code 2021",
        },
        {
          label: "Fire Code Edition",
          value: "2021 International Fire Code",
          codeSection: "General context and multiple chapter headings",
          source: "Dallas Fire Code 2021",
        },
      ],
    },
    {
      id: "permitting",
      number: "03",
      title: "Permitting",
      icon: "clipboard",
      dataPoints: [
        {
          label: "Permit Portal",
          value:
            "https://dallascityhall.com/departments/sustainabledevelopment/buildinginspection/Pages/BI_EPRS.aspx",
          codeSection: "Online Electronic Permitting",
          source: "Dallas Building Inspection, Online Electronic Permitting section",
        },
        {
          label: "Building Permit Expiration (months)",
          value: "24",
          codeSection: "Section 302.5",
          source: "Dallas Building Code, Section 302.5 Expiration of permit",
        },
        {
          label: "Building Permit Extension (months)",
          value: "4",
          codeSection: "Section 302.5.2 Extension of time",
          source: "Dallas City Code, Section 302.5.2",
        },
        {
          label: "Drawing Standards",
          value: "",
          bullets: [
            "Submittal Documents: Include construction docs, special inspection statements, geotechnical reports.",
            "Sheet Sizes: Final plans must be on 11x17 inch sheets.",
            "Ink Color: No mandated color; focus on legibility and clarity.",
            "Scale Requirements: Plans must be drawn to scale with clear, legible lettering.",
            "Title Block Information: Show location, work extent, structural/fire-resistive details, and seals.",
            "Digital Submission Standards: Permitted with official approval, must be legible and properly formatted.",
            "PDF Format Requirements: Final form on 11x17 sheets with revision and print dates.",
            "Layering Requirements: No specific CAD layering rules, clarity emphasized.",
          ],
          codeSection: "Sections 301.4.7, 7.4, 7.4.1, 7.4.2.3; Chapter 52, Section 301.4.7; File 251D-1",
          source:
            "Dallas Construction Codes, Sections 301.4.7 and 7.4; Dallas Building Code Sections 7.4.1 and 301.4.7; Dallas Development Code, Chapter 52, Section 301.4.7; Dallas Public Works Department Standard Construction Details File 251D-1",
        },
        {
          label: "File Specifications and Naming Conventions",
          value: "",
          bullets: [
            'All sheets must be 11" by 17" format',
            "Construction documents must be submitted in two or more sets",
            "Digital formats allowed if approved by fire code official",
            "Electronic submissions via city online plan review or email where allowed",
            "Fire alarm plans require at least two scaled copies and professional stamping",
            'Landscape plans require two blue or blackline prints on sheets up to 36" by 48"',
            'Site plans require reproducible prints and multiple folded copies on sheets up to 36" by 48"',
          ],
          codeSection: "Sections 7.4.1, 301.4.7, 302.2, 51A-10.123, [A] 106.2.1",
          source:
            "Dallas Building Department Regulations and Building Code, Sections 7.4.1, 301.4.7, 302.2, 51A-10.123, [A] 106.2.1",
        },
        {
          label: "Professional Certification",
          value: "",
          bullets: [
            "Texas registered architect/engineer seal required unless exemptions apply (small, residential, agricultural buildings under cost/size limits)",
            "Properly sealed plans may use wet stamp or digital signature",
            "Responsible registered design professional must prepare and seal required plans",
            "Special inspections and certifications require responsible professional's seal",
            "Responsible design professional must be identified in construction documents",
            "Building official may waive sealed documents if not necessary",
          ],
          codeSection: "Section 301.4.7",
          source: "Dallas Building Code, Chapter 52, Section 301.4.7",
        },
        {
          label: "Demolition Requirements",
          value: "",
          bullets: [
            "Notifications: Submit demolition permit, notify owners, occupants, utilities, and inspections.",
            "Escrow Requirements: Requires liability and auto insurance; no escrow required.",
            "Inspections: Notify building official; inspections at each stage and final approval.",
            "Permits: Obtain permit with detailed plans; non-transferable; may require related permits.",
            "Reporting: Include asbestos and insurance documentation; committee may require additional reports.",
            "Site Preparation and Safety: Relocate/cap utilities; protective devices; signage and dust control.",
            "Material Removal: Daily debris removal via approved routes and sites.",
          ],
          codeSection: "Sections 301.1, 301.4.4, 304.3; Dallas Fire Code, Section 3316.1",
          source:
            "Administrative Procedures for the Construction Codes, Sections 301.1, 301.4.4, 304.3; Dallas Fire Code, Section 3316.1",
        },
      ],
    },
    {
      id: "interconnection",
      number: "04",
      title: "Interconnection",
      icon: "plug",
      dataPoints: [
        {
          label: "Interconnection - Application Cost",
          value: "Required Impact Study Fee (no application fee):",
          bullets: [
            "Tier 1 (0 to 10 kW): $0.00 to $144.15",
            "Tier 2 (10+ to 500 kW): $296.30",
            "Tier 3 (500+ to 2000 kW): $3,960.30 to $6,623.45",
            "Tier 4 (2000+ kW) NON-EXPORTING: $6,927.85 to $9,591.00",
            "Tier 4 (2000+ kW) EXPORTING: $7,458.30 to $9,591.00",
          ],
          codeSection:
            "Section 6.1.2.4; Section 6.1.4.4; Distributed Generation Pre-Interconnection Study Schedule",
          source:
            "Oncor Electric Delivery Company LLC, Section 6.1.2.4 Distributed Generation Charges (DD36); Oncor Application for Interconnection: Distributed Generation Pre-Interconnection Study Fee Schedule",
        },
        {
          label: "Interconnection - Application Level",
          value: "",
          bullets: [
            "Tier 1 (0 to 10 kW)",
            "Tier 2 (10+ to 500 kW)",
            "Tier 3 (500+ to 2000 kW)",
            "Tier 4 (2000+ kW)",
          ],
          source: "Oncor Application for Interconnection",
        },
      ],
    },
    {
      id: "municipal-contact",
      number: "05",
      title: "Municipal Contact Information",
      icon: "landmark",
      dataPoints: [
        {
          label: "Building Department Address",
          value: "",
          bullets: [
            "Physical Address: 320 E Jefferson Blvd, Room 105, Dallas, TX 75203",
            "Phone Number: (214) 948-4480",
          ],
          codeSection: "Address and Contact Information",
          source: "City of Dallas Development Services Department, Address and Contact Information",
        },
        {
          label: "Fire Department Phone",
          value: "(214) 670-4319",
          codeSection: "Inspection & Life Safety Education Division",
          source: "Dallas Fire-Rescue Department, Inspection & Life Safety Education Division Contact",
        },
        {
          label: "Permit Department Phone",
          value: "(214) 948-4480",
          codeSection: "Contact Information",
          source: "City of Dallas Development Services Department, Contact Information",
        },
      ],
    },
  ],
  footnotes: [
    "ICC International Building Code, IBC-21",
    "Excerpt references to NFPA 70 and Dallas Electrical Code enforcement under 90.4 and working space requirements per NFPA 70 Section 110.26 as per Dallas Electrical Code 2021",
    "Dallas Fire Code 2021",
    "Dallas Energy Storage System regulations, Section 1207.8.3",
    "Dallas Development Code, Sections 51A-4.402(c)(5) and 51A-4.410(c)(6)",
    "International Fire Code, Sections 1205.5.1 and 1207.8.3; Dallas Fire Code 2021, Section 1207.8.3",
    "Dallas Fire Code 2021, Section 1207.8.3; General Commercial Zoning Standards; International Fire Code, Section 1207.10.7.3",
    "County Land Use Regulations, Section 9 Variance Procedure",
    "City of Dallas Development Services Department, Address and Contact Information",
    "Dallas Fire-Rescue Department, Inspection & Life Safety Education Division Contact",
    "City of Dallas Development Services Department, Contact Information",
    "Dallas Building Inspection, Online Electronic Permitting section",
    "Oncor Electric Delivery Company LLC, Section 6.1.2.4 Distributed Generation Charges (DD36); Oncor Application for Interconnection: Distributed Generation Pre-Interconnection Study Fee Schedule",
    "Dallas Building Code, Section 302.5 Expiration of permit",
    "Dallas City Code, Section 302.5.2",
    "Dallas Construction Codes, Sections 301.4.7 and 7.4; Dallas Building Code Sections 7.4.1 and 301.4.7; Dallas Development Code, Chapter 52, Section 301.4.7; Dallas Public Works Department Standard Construction Details File 251D-1",
    "Dallas Building Department Regulations and Building Code, Sections 7.4.1, 301.4.7, 302.2, 51A-10.123, [A] 106.2.1",
    "Dallas Building Code, Chapter 52, Section 301.4.7",
    "Administrative Procedures for the Construction Codes, Sections 301.1, 301.4.4, 304.3; Dallas Fire Code, Section 3316.1",
  ],
};

export const CITADEL_REPORT: SampleReport = {
  slug: "citadel",
  reportId: "FR-84B01C7A",
  kicker: "Feasibility Report",
  title: "West Sacramento (Residential)",
  prospect: "Citadel Roofing & Solar",
  prospectUrl: "https://citadelrs.com/",
  jurisdiction: "West Sacramento, CA",
  county: "Yolo County",
  city: "West Sacramento",
  projectType: "Residential",
  systemSize: "7.4 kW",
  utility: "Pacific Gas & Electric (PG&E)",
  reportRun: "Jul 28, 2026",
  verifiedLabel: "Jul 2026 · Seal of Authenticity",
  sections: [
    {
      id: "design",
      number: "01",
      title: "Design and Engineering",
      icon: "ruler",
      dataPoints: [
        {
          label: "BESS Aggregate kWh Limit",
          value: "",
          bullets: [
            "Interior spaces: 40 kWh",
            "Attached garages: 80 kWh",
            "Exterior near walls: 100 kWh",
            "Exterior near walls with noncombustible: 200 kWh",
            "Detached garages/accessory structures: 200 kWh",
            "Detached garages/accessory 10+ feet from lines: 600 kWh",
            "Outdoors on ground: 200 kWh",
            "Outdoors on ground 10+ feet from lines: 600 kWh",
            "Total aggregate limit on property: 600 kWh",
          ],
          codeSection: "Section 1207.11.4",
          source: "California Fire Code, Section 1207.11.4",
        },
        {
          label: "Solar Rooftop Fire Access Pathways",
          value: "",
          bullets: [
            "Distance to ridge (arrays ≤33% roof area): 18",
            "Distance to ridge (arrays >33% roof area): 36",
            "Eave to ridge path width: 36",
            "Distance to skylights: 36",
          ],
          codeSection: "Sections R329.6 and 1205.2.1",
          source: "California Residential Code, Sections R329.6 and 1205.2.1",
        },
      ],
    },
    {
      id: "permitting",
      number: "02",
      title: "Permitting",
      icon: "clipboard",
      dataPoints: [
        {
          label: "Solar Expedited Permitting",
          value: "",
          bullets: [
            "Expedited Platform: SolarAPP+",
            "Applies to: Solar; Solar + Storage",
            "Does not apply to: EV chargers",
          ],
          codeSection: "SolarAPP+ Automated Solar Plan Reviews",
          source: "City of West Sacramento Building Fee Schedule and SolarAPP+ Automated Solar Plan Reviews",
        },
        {
          label: "Solar Site Plan",
          value: "",
          bullets: [
            "Page Requirements: Drawn to scale",
            "Page Requirements: North arrow",
            "Building/Site Identification: Property boundaries and dwelling location",
            "Building/Site Identification: Side yards and setback distances",
            "Building/Site Identification: Roofing material type",
            "Solar Panel Details: Location of solar panels on roof or yard",
            "Solar Panel Details: Total number of solar panels",
            "Hardware Identification: Electrical service, junction boxes, disconnect, inverter location",
            "Hardware Identification: Roof framing members type, size, and spacing",
            "Hardware Identification: Conduit location, sizes, and routing pathway",
            "Site Features: Gates or fencing materials and heights",
          ],
          codeSection: "Roof Mount Photovoltaic Checklist",
          source: "City of West Sacramento Roof Mount Photovoltaic Checklist",
        },
        {
          label: "Solar Permit Cost",
          value: "MISCELLANEOUS CONSTRUCTION PERMIT FEES — RESIDENTIAL: SINGLE-FAMILY, DUPLEX, ADU, JADU",
          bullets: [
            "Solar: $450 ≤15 kW; plus $15/kW above 15 kW",
            "Storage: $445",
            "Solar + Storage: $586",
          ],
          codeSection: "Miscellaneous Construction Permit Fees — Residential",
          source:
            "City of West Sacramento Permit Fee Schedule, MISCELLANEOUS CONSTRUCTION PERMIT FEES RESIDENTIAL: SINGLE-FAMILY, DUPLEX, ADU, JADU",
        },
      ],
    },
    {
      id: "interconnection",
      number: "03",
      title: "Interconnection",
      icon: "plug",
      dataPoints: [
        {
          label: "Solar Max DC System Size",
          value: "30 kW",
          source:
            "Pacific Gas & Electric (PG&E), AGREEMENT AND CUSTOMER AUTHORIZATION Net Energy Metering Interconnection For Solar And/Or Wind Electric Generating Facilities Of 30 Kilowatts Or Less",
        },
      ],
    },
    {
      id: "licensing",
      number: "04",
      title: "Licensing",
      icon: "badge",
      dataPoints: [
        {
          label: "Business License Expiration Timeline",
          value: "12",
          codeSection: "Business License Renewal Period",
          source: "West Sacramento City Code or Ordinance (inferred from analysis)",
        },
        {
          label: "Business License Fee",
          value: "421.16",
          codeSection: "Business License Fees",
          source: "Business License & Planning Fees (Updated November 1, 2025)",
        },
        {
          label: "Business License Portal",
          value: "https://aca-prod.accela.com/WESTSAC/Default.aspx",
          codeSection: "Business License Renewal Online URL",
          source: "City of West Sacramento Business Portal",
        },
        {
          label: "Business License Renewal Date",
          value: "Date of Issuance",
          codeSection: "Business License Fees",
          source: "Business License & Planning Fees (Updated November 1, 2025)",
        },
      ],
    },
    {
      id: "inspections",
      number: "05",
      title: "Inspections",
      icon: "search",
      dataPoints: [
        {
          label: "Re-inspection Fee",
          value: "152",
          codeSection: "Reinspection / Additional Inspection Fee",
          source:
            "Building Fee Schedule Effective November 1, 2025, Reinspection / Additional Inspection Fee; City of West Sacramento Building Fee Schedule, Section Re-inspection / Additional Inspection Fee",
        },
      ],
    },
  ],
  footnotes: [
    "West Sacramento City Code or Ordinance (inferred from analysis)",
    "Business License & Planning Fees (Updated November 1, 2025)",
    "City of West Sacramento Business Portal",
    "City of West Sacramento Building Fee Schedule and SolarAPP+ Automated Solar Plan Reviews",
    "City of West Sacramento Roof Mount Photovoltaic Checklist",
    "City of West Sacramento Permit Fee Schedule, MISCELLANEOUS CONSTRUCTION PERMIT FEES RESIDENTIAL: SINGLE-FAMILY, DUPLEX, ADU, JADU",
    "Building Fee Schedule Effective November 1, 2025, Reinspection / Additional Inspection Fee; City of West Sacramento Building Fee Schedule, Section Re-inspection / Additional Inspection Fee",
    "California Fire Code, Section 1207.11.4",
    "California Residential Code, Sections R329.6 and 1205.2.1",
    "Pacific Gas & Electric (PG&E), AGREEMENT AND CUSTOMER AUTHORIZATION Net Energy Metering Interconnection For Solar And/Or Wind Electric Generating Facilities Of 30 Kilowatts Or Less",
  ],
};

export const CHARGE_REPORT: SampleReport = {
  slug: "charge",
  reportId: "CE-19A7D3F2",
  kicker: "Comprehensive Report",
  title: "Dallas, TX — C&I Solar + Storage",
  prospect: "Charge Energy Solutions",
  prospectUrl: "https://chargeenergy.com/",
  jurisdiction: "Dallas, TX",
  county: "Dallas County",
  city: "Dallas",
  projectType: "C&I",
  systemSize: "Commercial & Industrial",
  utility: "Oncor",
  reportRun: "Aug 10, 2026",
  verifiedLabel: "Aug 2026 · Seal of Authenticity",
  sections: [
    {
      id: "design",
      number: "01",
      title: "Design and Engineering",
      icon: "ruler",
      dataPoints: [
        {
          label: "Wind Speed",
          value: "115 mph",
          codeSection: "1609.3",
          source: "Dallas Building Code 2021, Section 1609.3",
        },
        {
          label: "Ground Snow Load",
          value: "5 psf",
          codeSection: "Section 1608.2 Ground Snow Loads",
          source: "International Building Code, Section 1608.2",
        },
      ],
    },
    {
      id: "zoning",
      number: "02",
      title: "Zoning & Land Use",
      icon: "layers",
      dataPoints: [
        {
          label: "Solar Permissible Zone",
          value: "",
          bullets: [
            "Permitted by Right: Office Districts (O-1, O-2, NO, LO, MO, GO), Commercial Districts (SC, GR, HC), Central Area (E) Districts (CA-1, CA-2), Industrial Districts (I-1, I-2, I-3)",
            "Conditional Approval: Some districts requiring Specific Use Permits or Limited Uses",
            "Conditional Approval Process: Specific Use Permit (SUP), Limited Uses [L] approvals",
          ],
          codeSection: "Sections 51A-4.217 and 51A-13.306(5) (E)",
          source:
            "Dallas Development Code, Sections 51A-4.217, 51A-13.306(5)(E), 51A-4.202, 51A-4.204, 51A-4.105",
        },
        {
          label: "BESS Restricted Zones",
          value: "",
          bullets: [
            "Residential (unless special use permitted)",
            "O-1 Office District (unless special use permitted)",
            "NO Neighborhood Office District (unless special use permitted)",
          ],
          codeSection: "Sections 51A-4.212 and 51A-4.217",
          source: "Dallas Development Code, Sections 51A-4.212 and 51A-4.217",
        },
        {
          label: "Non-Residential BESS Setback from Non-Participating Property Line",
          value: "10 feet",
          codeSection: "Section 1207.8.3",
          source: "Dallas Energy Storage System regulations, Section 1207.8.3",
        },
        {
          label: "Non-Residential BESS Setback from Non-Participating Occupied Structure",
          value: "10 feet",
          codeSection: "Sections 51A-4.402(c) (5) and 51A-4.410(c) (6)",
          source: "Dallas Development Code, Sections 51A-4.402(c)(5) and 51A-4.410(c)(6)",
        },
        {
          label: "Solar Property Line Requirements",
          value: "",
          bullets: [
            "Buffers: Clear, brush-free 10-foot perimeter around photovoltaic arrays with approved surface",
          ],
          codeSection: "Sections 1205.5.1 and 1207.8.3",
          source: "International Fire Code, Sections 1205.5.1 and 1207.8.3; Dallas Fire Code 2021, Section 1207.8.3",
        },
        {
          label: "Solar Property Line Setback Distance",
          value: "10 feet",
          codeSection: "Section 1207.8.3; Side Setbacks",
          source:
            "Dallas Fire Code 2021, Section 1207.8.3; General Commercial Zoning Standards; International Fire Code, Section 1207.10.7.3",
        },
        {
          label: "Solar Property Line Variances",
          value: "Yes",
          codeSection: "Section 9",
          source: "County Land Use Regulations, Section 9 Variance Procedure",
        },
        {
          label: "Solar Road Setback",
          value: "10 feet",
          codeSection: "Section 1205.5; Section 1207.5.7; Section 1207.8.3",
          source: "Dallas Solar Photovoltaic Power Systems Code, Section 1205.5; Dallas Fire Code, Sections 1207.5.7 and 1207.8.3",
        },
        {
          label: "Solar Decommissioning Financial Assurance",
          value: "",
          bullets: [
            "Application: Applies to commercial ground-mounted solar farms with energy storage systems requiring decommissioning plans",
            "Form: Performance bond or irrevocable letter of credit payable to the city",
            "Amount: No less than 100% of total infrastructure repair costs as determined by city director",
          ],
          codeSection: "Section 1207.2.3; Relevant Zoning and Development Code Sections",
          source:
            "City of Dallas Codes and Fire Code, Sections 1207.2.3; Zoning Code related to Commercial Ground-Mounted Solar; Development Code",
        },
        {
          label: "Solar Decommissioning Requirements",
          value: "",
          bullets: [
            "Equipment Removal: Remove all energy storage system components safely.",
            "Site Restoration: Restore site to a safe, non-hazardous, pre-installation condition.",
            "Soil Restoration: Apply soil management standards including testing and amendments.",
            "Hazardous Materials Management: Submit closure plan 30 days prior for hazardous materials handling.",
          ],
          codeSection: "Sections 1207.2.3, 1207.1.3, 5001.6.3, 1207, 1201, 4.5.5",
          source:
            "Dallas Fire Code, Sections 1207.2.3, 1207.1.3, 5001.6.3; International Fire Code, Sections 1207 and 1201; Dallas Development Code, Sections 1207.1.3, 1207.2.3, and 4.5.5",
        },
        {
          label: "BESS Walkway Setback",
          value: "10 feet",
          codeSection: "Section 1207.8.3",
          source:
            "Dallas Energy Storage System (ESS) requirements; International Fire Code; Dallas Fire Code and Building Code, Section 1207.8.3",
        },
      ],
    },
    {
      id: "code-cycle",
      number: "03",
      title: "Code Cycle",
      icon: "book",
      dataPoints: [
        {
          label: "Building Code Edition",
          value: "2021 International Building Code",
          codeSection: "IBC-21",
          source: "ICC International Building Code, IBC-21",
        },
        {
          label: "Electrical Code Edition",
          value: "2023 National Electrical Code",
          codeSection: "Dallas Electrical Code 2021, referencing NFPA 70",
          source:
            "Excerpt references to NFPA 70 and Dallas Electrical Code enforcement under 90.4 and working space requirements per NFPA 70 Section 110.26 as per Dallas Electrical Code 2021",
        },
        {
          label: "Fire Code Edition",
          value: "2021 International Fire Code",
          codeSection: "General context and multiple chapter headings",
          source: "Dallas Fire Code 2021",
        },
      ],
    },
    {
      id: "permitting",
      number: "04",
      title: "Permitting",
      icon: "clipboard",
      dataPoints: [
        {
          label: "Permit Portal",
          value:
            "https://dallascityhall.com/departments/sustainabledevelopment/buildinginspection/Pages/BI_EPRS.aspx",
          codeSection: "Online Electronic Permitting",
          source: "Dallas Building Inspection, Online Electronic Permitting section",
        },
        {
          label: "Building Permit Expiration (months)",
          value: "24",
          codeSection: "Section 302.5",
          source: "Dallas Building Code, Section 302.5 Expiration of permit",
        },
        {
          label: "Building Permit Extension (months)",
          value: "4",
          codeSection: "Section 302.5.2 Extension of time",
          source: "Dallas City Code, Section 302.5.2",
        },
        {
          label: "Drawing Standards",
          value: "",
          bullets: [
            "Submittal Documents: Include construction docs, special inspection statements, geotechnical reports.",
            "Sheet Sizes: Final plans must be on 11x17 inch sheets.",
            "Ink Color: No mandated color; focus on legibility and clarity.",
            "Scale Requirements: Plans must be drawn to scale with clear, legible lettering.",
            "Title Block Information: Show location, work extent, structural/fire-resistive details, and seals.",
            "Digital Submission Standards: Permitted with official approval, must be legible and properly formatted.",
            "PDF Format Requirements: Final form on 11x17 sheets with revision and print dates.",
            "Layering Requirements: No specific CAD layering rules, clarity emphasized.",
          ],
          codeSection: "Sections 301.4.7, 7.4, 7.4.1, 7.4.2.3; Chapter 52, Section 301.4.7; File 251D-1",
          source:
            "Dallas Construction Codes, Sections 301.4.7 and 7.4; Dallas Building Code Sections 7.4.1 and 301.4.7; Dallas Development Code, Chapter 52, Section 301.4.7; Dallas Public Works Department Standard Construction Details File 251D-1",
        },
        {
          label: "File Specifications and Naming Conventions",
          value: "",
          bullets: [
            'All sheets must be 11" by 17" format',
            "Construction documents must be submitted in two or more sets",
            "Digital formats allowed if approved by fire code official",
            "Electronic submissions via city online plan review or email where allowed",
            "Fire alarm plans require at least two scaled copies and professional stamping",
            'Landscape plans require two blue or blackline prints on sheets up to 36" by 48"',
            'Site plans require reproducible prints and multiple folded copies on sheets up to 36" by 48"',
          ],
          codeSection: "Sections 7.4.1, 301.4.7, 302.2, 51A-10.123, [A] 106.2.1",
          source:
            "Dallas Building Department Regulations and Building Code, Sections 7.4.1, 301.4.7, 302.2, 51A-10.123, [A] 106.2.1",
        },
        {
          label: "Professional Certification",
          value: "",
          bullets: [
            "Texas registered architect/engineer seal required unless exemptions apply (small, residential, agricultural buildings under cost/size limits)",
            "Properly sealed plans may use wet stamp or digital signature",
            "Responsible registered design professional must prepare and seal required plans",
            "Special inspections and certifications require responsible professional's seal",
            "Responsible design professional must be identified in construction documents",
            "Building official may waive sealed documents if not necessary",
          ],
          codeSection: "Section 301.4.7",
          source: "Dallas Building Code, Chapter 52, Section 301.4.7",
        },
        {
          label: "Demolition Requirements",
          value: "",
          bullets: [
            "Notifications: Submit demolition permit, notify owners, occupants, utilities, and inspections.",
            "Escrow Requirements: Requires liability and auto insurance; no escrow required.",
            "Inspections: Notify building official; inspections at each stage and final approval.",
            "Permits: Obtain permit with detailed plans; non-transferable; may require related permits.",
            "Reporting: Include asbestos and insurance documentation; committee may require additional reports.",
            "Site Preparation and Safety: Relocate/cap utilities; protective devices; signage and dust control.",
            "Material Removal: Daily debris removal via approved routes and sites.",
          ],
          codeSection: "Sections 301.1, 301.4.4, 304.3; Dallas Fire Code, Section 3316.1",
          source:
            "Administrative Procedures for the Construction Codes, Sections 301.1, 301.4.4, 304.3; Dallas Fire Code, Section 3316.1",
        },
      ],
    },
    {
      id: "interconnection",
      number: "05",
      title: "Interconnection",
      icon: "plug",
      dataPoints: [
        {
          label: "Interconnection - Application Cost",
          value: "Required Impact Study Fee (no application fee):",
          bullets: [
            "Tier 1 (0 to 10 kW): $0.00 to $144.15",
            "Tier 2 (10+ to 500 kW): $296.30",
            "Tier 3 (500+ to 2000 kW): $3,960.30 to $6,623.45",
            "Tier 4 (2000+ kW) NON-EXPORTING: $6,927.85 to $9,591.00",
            "Tier 4 (2000+ kW) EXPORTING: $7,458.30 to $9,591.00",
          ],
          codeSection:
            "Section 6.1.2.4; Section 6.1.4.4; Distributed Generation Pre-Interconnection Study Fee Schedule",
          source:
            "Oncor Electric Delivery Company LLC, Section 6.1.2.4 Distributed Generation Charges (DD36); Oncor Application for Interconnection; Oncor Application for Interconnection: Distributed Generation Pre-Interconnection Study Fee Schedule",
        },
        {
          label: "Interconnection - Application Level",
          value: "",
          bullets: [
            "Tier 1 (0 to 10 kW)",
            "Tier 2 (10+ to 500 kW)",
            "Tier 3 (500+ to 2000 kW)",
            "Tier 4 (2000+ kW)",
          ],
          source: "Oncor Application for Interconnection",
        },
        {
          label: "Interconnection - Line Diagram Submittal Guidelines",
          value: "",
          bullets: [
            "One Line Diagram and Layout Sketch",
            "Diagram must be one-page PDF, flattened, non-editable, under 2MB",
            "Two pages allowed if placarding is included",
            "Service address shown clearly",
            "Panels, Inverters, Batteries included",
            "Visible Lockable Labeled AC Disconnect (VLLD) included",
            "Oncor Utility Meter shown and distinguished",
            "Include manufacturer, model, quantity, voltage",
            "Separate VLLD between customer equipment and Oncor meter",
            "Existing equipment details included",
            '"Visible Lockable Labeled Disconnect" spelled out before acronym "VLLD"',
            "VLLD located on accessible exterior wall within 10 feet of Oncor Meter",
            "Exact distance between AC Disconnect and Oncor Meter noted",
            "Class 2 or 3 placards if VLLD over 10 feet away",
            "Class 1 placard proofs if VLLD within 10 feet",
            "ESI ID number or 7-digit premise number shown",
            "Show order of equipment connections",
            "ATS mode if generator connected (Open or Closed transition)",
            "No extraneous or irrelevant details included",
          ],
          codeSection: "One Line Diagram and Layout Sketch",
          source: "Oncor Distributed Generation Interconnection Portal Documents, One Line Diagram and Layout Sketch",
        },
        {
          label: "Interconnection - Submittal Documents",
          value: "",
          bullets: [
            "Interconnection Application 6.3 (tariff document)",
            "Pre-Interconnection Study Fee",
            "One-line diagram",
            "Relay functional diagram",
            "Layout sketch (showing the Point of Common Coupling (PCC) in relation to your generation)",
            "Preliminary relay settings",
          ],
          codeSection: "Application Check List",
          source: "Oncor Application for Interconnection of Distributed Generation",
        },
        {
          label: "Application Review Timeline",
          value: "",
          bullets: [
            "Tier 1: 30 days",
            "Tier 2: 30 days",
            "Tier 3: No guaranteed timeline",
            "Tier 4: No guaranteed timeline",
          ],
          codeSection:
            "Sections 'What is a certified or non-certified distributed renewable generation (DRG) system?' and Oncor Solar Interconnection Information",
          source:
            "Oncor Renewable Energy Information, Sections 'What is a certified or non-certified distributed renewable generation (DRG) system?' and Oncor Solar Interconnection Information",
        },
      ],
    },
    {
      id: "municipal-contact",
      number: "06",
      title: "Municipal Contact Information",
      icon: "landmark",
      dataPoints: [
        {
          label: "Building Department Address",
          value: "",
          bullets: [
            "Physical Address: 320 E Jefferson Blvd, Room 105, Dallas, TX 75203",
            "Phone Number: (214) 948-4480",
          ],
          codeSection: "Address and Contact Information",
          source: "City of Dallas Development Services Department, Address and Contact Information",
        },
        {
          label: "Fire Department Phone",
          value: "(214) 670-4319",
          codeSection: "Inspection & Life Safety Education Division",
          source: "Dallas Fire-Rescue Department, Inspection & Life Safety Education Division Contact",
        },
        {
          label: "Permit Department Phone",
          value: "(214) 948-4480",
          codeSection: "Contact Information",
          source: "City of Dallas Development Services Department, Contact Information",
        },
      ],
    },
  ],
  footnotes: [
    "ICC International Building Code, IBC-21",
    "Excerpt references to NFPA 70 and Dallas Electrical Code enforcement under 90.4 and working space requirements per NFPA 70 Section 110.26 as per Dallas Electrical Code 2021",
    "Dallas Fire Code 2021",
    "Dallas Development Code, Sections 51A-4.217, 51A-13.306(5)(E), 51A-4.202, 51A-4.204, 51A-4.105",
    "Dallas Development Code, Sections 51A-4.212 and 51A-4.217",
    "Dallas Energy Storage System regulations, Section 1207.8.3",
    "Dallas Development Code, Sections 51A-4.402(c)(5) and 51A-4.410(c)(6)",
    "International Fire Code, Sections 1205.5.1 and 1207.8.3; Dallas Fire Code 2021, Section 1207.8.3",
    "Dallas Fire Code 2021, Section 1207.8.3; General Commercial Zoning Standards; International Fire Code, Section 1207.10.7.3",
    "County Land Use Regulations, Section 9 Variance Procedure",
    "Dallas Solar Photovoltaic Power Systems Code, Section 1205.5; Dallas Fire Code, Sections 1207.5.7 and 1207.8.3",
    "City of Dallas Codes and Fire Code, Sections 1207.2.3; Zoning Code related to Commercial Ground-Mounted Solar; Development Code",
    "Dallas Fire Code, Sections 1207.2.3, 1207.1.3, 5001.6.3; International Fire Code, Sections 1207 and 1201; Dallas Development Code, Sections 1207.1.3, 1207.2.3, and 4.5.5",
    "Dallas Energy Storage System (ESS) requirements; International Fire Code; Dallas Fire Code and Building Code, Section 1207.8.3",
    "City of Dallas Development Services Department, Address and Contact Information",
    "Dallas Fire-Rescue Department, Inspection & Life Safety Education Division Contact",
    "Dallas Building Inspection, Online Electronic Permitting section",
    "Oncor Electric Delivery Company LLC, Section 6.1.2.4 Distributed Generation Charges (DD36); Oncor Application for Interconnection; Oncor Application for Interconnection: Distributed Generation Pre-Interconnection Study Fee Schedule",
    "Oncor Distributed Generation Interconnection Portal Documents, One Line Diagram and Layout Sketch",
    "Oncor Application for Interconnection of Distributed Generation",
    "Dallas Building Code, Section 302.5 Expiration of permit",
    "Dallas City Code, Section 302.5.2",
    "Dallas Construction Codes, Sections 301.4.7 and 7.4; Dallas Building Code Sections 7.4.1 and 301.4.7; Dallas Development Code, Chapter 52, Section 301.4.7; Dallas Public Works Department Standard Construction Details File 251D-1",
    "Dallas Building Department Regulations and Building Code, Sections 7.4.1, 301.4.7, 302.2, 51A-10.123, [A] 106.2.1",
    "Dallas Building Code, Chapter 52, Section 301.4.7",
    "Administrative Procedures for the Construction Codes, Sections 301.1, 301.4.4, 304.3; Dallas Fire Code, Section 3316.1",
    "Oncor Renewable Energy Information, Sections 'What is a certified or non-certified distributed renewable generation (DRG) system?' and Oncor Solar Interconnection Information",
  ],
};

export const PIVOT_REPORT: SampleReport = {
  slug: "pivot",
  reportId: "PE-20B4C7A1",
  kicker: "Comprehensive Report",
  title: "Matteson, IL — 1.5 MW  Solar + Storage",
  prospect: "Pivot Energy",
  prospectUrl: "https://www.pivotenergy.net/",
  jurisdiction: "Matteson, IL",
  county: "Cook County",
  city: "Matteson",
  projectType: "C&I",
  systemSize: "1.5 MW DC",
  utility: "Commonwealth Edison (ComEd)",
  reportRun: "Aug 11, 2026",
  verifiedLabel: "Aug 2026 · Seal of Authenticity",
  sections: [
    {
      id: "design",
      number: "01",
      title: "Design and Engineering",
      icon: "ruler",
      dataPoints: [
        {
          label: "Wind Speed",
          value: "115 mph",
          codeSection: "Table R301.2(1)",
          source: "Village of Matteson Residential Code Amendments, Table R301.2(1)",
        },
        {
          label: "Ground Snow Load",
          value: "30 psf",
          codeSection: "Table R301.2(1)",
          source: "Village of Matteson Residential Code, Table R301.2(1)",
        },
      ],
    },
    {
      id: "zoning",
      number: "02",
      title: "Zoning & Land Use",
      icon: "layers",
      dataPoints: [
        {
          label: "Solar Permissible Zone",
          value: "",
          bullets: [
            "Permitted by Right: Residential R-1 through R-7; Commercial C-1 through C-4; Mixed Use MXD; Industrial I-1 through I-3",
            "Conditional Approval: Commercial C-1 through C-5; Mixed Use MXD; Industrial I-1 through I-3",
            "Conditional Approval Process: Special Use Permit",
          ],
          codeSection: "Sections 159.22, 159.05, 159.10, and 159.60",
          source: "Village of Matteson Zoning Code Ordinance, Sections 159.22, 159.05, 159.10, and 159.60",
        },
        {
          label: "BESS Restricted Zones",
          value: "",
          bullets: [
            "Residential (unless special use permitted)",
            "Commercial (unless special use permitted)",
            "Mixed Use (unless special use permitted)",
          ],
          codeSection: "Sections 159.05, 159.10, and 159.22",
          source: "Village of Matteson Zoning Code Ordinance, Sections 159.05, 159.10, and 159.22",
        },
        {
          label: "Non-Residential BESS Setback from Non-Participating Property Line",
          value: "50 feet",
          codeSection: "Section 5; Section (f)",
          source: "SB3409 - Public Act 102-1123, Section 5",
        },
        {
          label: "Non-Residential BESS Setback from Non-Participating Occupied Structure",
          value: "150 feet",
          codeSection: "Section (f)",
          source: "SB3409 - Public Act 102-1123",
        },
        {
          label: "BESS Walkway Setback",
          value: "50 feet",
          source: "Illinois Public Act 102-1123",
        },
        {
          label: "Solar Property Line Requirements",
          value: "",
          bullets: [
            "Buffers: Vegetative screening may be required, no berms mandated",
            "Buffers: Pollinator-friendly vegetation and management plans may be required",
            "Fences: Perimeter fencing between 6 and 25 feet in height",
            "Panel Height Restrictions: Solar panels max height 20 feet at full tilt",
            "Sound: Sound limits match Illinois Pollution Control Board standards",
          ],
          codeSection: "Section (e), (l)",
          source: "SB3409 - Public Act 102-1123",
        },
        {
          label: "Solar Property Line Setback Distance",
          value: "50 feet",
          codeSection: "Section on Commercial Solar Energy Facility Setbacks",
          source: "Public Act 102-1123, HB4412 Enrolled",
        },
        {
          label: "Solar Property Line Variances",
          value: "Yes",
          codeSection: "Section related to Variances and Zoning Board of Appeals",
          source: "Village of Matteson Zoning Code",
        },
        {
          label: "Solar Road Setback",
          value: "50 feet",
          codeSection: "Section 15(f); Table 6",
          source:
            "Renewable Energy Facilities Agricultural Impact Mitigation Act, Section 15(f); Village of Matteson Zoning Code Ordinance, Table 6; County Zoning Ordinance, Section on Energy Storage System Setbacks",
        },
        {
          label: "Solar Decommissioning Financial Assurance",
          value: "",
          bullets: [
            "Application: Applies to commercial ground-mounted solar farms, including leased land",
            "Form: Must be a reclamation bond, surety bond, performance bond, letter of credit, escrow, or other acceptable financial assurance",
            "Amount: Max equals estimated decommissioning cost minus salvage value, phased payments 25%, 50%, and 100%",
            "Update Cadence: Updated every 5 years or less based on revised engineer estimates",
          ],
          codeSection: "Sections 10 and 15",
          source: "Renewable Energy Facilities Agricultural Impact Mitigation Act, Sections 10 and 15",
        },
        {
          label: "Solar Decommissioning Requirements",
          value: "",
          bullets: [
            "Equipment Removal: Remove all non-functional structures and debris",
            "Equipment Removal: Reuse or recycle equipment when commercially reasonable",
            "Equipment Removal: Complete decommissioning within 18 months of facility end",
            "Equipment Removal: Provide financial assurance for site restoration",
            "Site Restoration: Remove support structures and debris to 5 feet depth",
            "Site Restoration: Restore soil and vegetation close to preconstruction state",
            "Site Restoration: Repair agricultural drainage, compaction, rutting, erosion",
            "Site Restoration: Control weeds, clear trees, repair access roads",
            "Site Restoration: Maintain agricultural impact mitigation agreement on-site",
            "Soil Restoration: Remove and replace topsoil",
            "Soil Restoration: Repair compaction, rutting, and level land",
            "Soil Restoration: Prevent soil erosion and repair conservation practices",
            "Soil Restoration: Reroute and repair agricultural drainage tiles",
            "Soil Restoration: Remove rocks and control weeds",
          ],
          codeSection: "Sections 10 and 15",
          source: "Renewable Energy Facilities Agricultural Impact Mitigation Act, 505 ILCS 147, Sections 10 and 15",
        },
        {
          label: "Solar Decommissioning Timeline",
          value: "18 months",
          codeSection: "505 ILCS 147/10 and 147/15",
          source: "Renewable Energy Facilities Agricultural Impact Mitigation Act, 505 ILCS 147/10 and 147/15",
        },
      ],
    },
    {
      id: "code-cycle",
      number: "03",
      title: "Code Cycle",
      icon: "book",
      dataPoints: [
        {
          label: "Building Code Edition",
          value: "2018 International Building Code",
          codeSection: "Section 150.001",
          source: "Village of Matteson Municipal Code, Section 150.001",
        },
        {
          label: "Electrical Code Edition",
          value: "2017 National Electrical Code",
          codeSection: "§ 150.045 Adoption of Electrical Code",
          source: "Village of Matteson Municipal Code, § 150.045",
        },
        {
          label: "Fire Code Edition",
          value: "2018 International Fire Code",
          codeSection: "Section 95.01",
          source: "Village of Matteson Code, Section 95.01",
        },
      ],
    },
    {
      id: "permitting",
      number: "04",
      title: "Permitting",
      icon: "clipboard",
      dataPoints: [
        {
          label: "Permit Portal",
          value: "http://www.citizenserve.com/matteson",
          source: "Village of Matteson Donation/Collection Container Permit Requirements Policy",
        },
        {
          label: "Building Permit Expiration (months)",
          value: "6",
          codeSection: "Section 154.024",
          source: "Village of Matteson Zoning Code Ordinance, Section 154.024",
        },
        {
          label: "Building Permit Extension (months)",
          value: "24",
          codeSection: "Institutional/Special Residence Permit Extension provisions",
          source: "Village of Matteson Zoning Code Ordinance",
        },
        {
          label: "Drawing Standards",
          value: "",
          bullets: [
            "Sheet sizes: 24\" x 36\" full-sized working sheets for all design stages",
            "Ink color: No specific requirements",
            "Scale requirements: Drawings must be to scale; ratios not specified",
            "Title block info: Include vicinity map, scale, north arrow, engineer's title block",
            "Title block info continued: USGS benchmark, engineer seal, revision dates, project number",
            "Digital submissions: PDF allowed if approved by code official, sealed and stamped",
            "Layering: Start with cover sheet, include north arrow, seals, easements, spot elevations",
          ],
          codeSection: "Multiple sections as listed",
          source:
            "Matteson Construction Documentation Requirements; Village of Matteson Commercial Construction Drawing Standards; Sections 113.022 and R103",
        },
        {
          label: "File Specifications and Naming Conventions",
          value: "",
          bullets: [
            "24\" x 36\" sheet size for all design stages",
            "Minimum three sets of plans per permit application",
            "Electronic submissions only with approval; PDF format required",
            "Paper submissions bound in 3-ring binders, tabbed by section",
          ],
          codeSection: "Section 27.1.1.2; Section D Submittal Requirements; Section IV Engineering Plan Requirements",
          source:
            "Illinois Energy Efficient Building Code - 2018, Section 27.1.1.2; Village of Matteson Commercial/Industrial Checklist for Build Outs and Change of Use",
        },
        {
          label: "Professional Certification",
          value: "",
          bullets: [
            "Architect/engineer seal required on construction compliance materials",
            "Seals must comply with Illinois Architectural Practice and Engineering Acts",
            "Construction documents prepared by registered design professional",
            "Responsible professional info in title block: seal, signature, firm contact, project title",
          ],
          codeSection: "Section C103.1",
          source: "Illinois Energy Conservation Code, Section C103.1; Illinois Building Code, Section C103.1",
        },
        {
          label: "Demolition Requirements",
          value: "",
          bullets: [
            "Notifications: Code official orders demolition for unsafe structures, notices and postings required",
            "Escrow Requirements: No separate escrow; costs charged as liens if owner fails to comply",
            "Inspections: Required during and after demolition, 24-hour notice required",
            "Permits: Demolition permit required, contractors must be licensed, fees paid upfront, multiple department approvals needed",
            "Reporting: Salvage sale reports and test reports must be submitted and retained",
            "Failure to Comply: Demolition costs charged as lien if orders ignored",
            "Additional: Utility disconnection allowed for safety, hearing and appeal rights exist",
          ],
          source:
            "Village of Matteson Code, Section 20-70.060; Village of Matteson Commercial/Industrial Checklist for Build Outs and Change of Use",
        },
      ],
    },
    {
      id: "interconnection",
      number: "05",
      title: "Interconnection",
      icon: "plug",
      dataPoints: [
        {
          label: "Interconnection - Application Cost",
          value: "",
          bullets: [
            "Level 1: $50.00",
            "Level 2: $100.00 plus $1.00 per kVA",
            "Level 3: $500.00 plus $2.00 per kVA",
            "Level 4: $1,000.00 plus $2.00 per kVA",
          ],
          codeSection: "Sections on Application Fees and Interconnection Levels",
          source: "ComEd Distributed Energy Resource Interconnection Guidelines",
        },
        {
          label: "Interconnection - Application Level",
          value: "",
          bullets: [
            "Tier 1: 25 kW",
            "Tier 2: 5 MW",
            "Tier 3: 50 kW (Non-export systems)",
            "Tier 4: 10 MVA",
          ],
          codeSection: "Part 466, Section 466.80(b), 466.80(c)",
          source: "Illinois Administrative Code Part 466; ComEd DER Interconnection Guidelines",
        },
        {
          label: "Interconnection - Line Diagram Submittal Guidelines",
          value: "",
          bullets: [
            "Page Requirements: Completed Relay Functional Requirements Specifications (RFRS) form",
            "Electrical one-line diagram showing all equipment and connections",
            "Three-line diagrams for Level 3 interconnections",
            "Proposed CT/PT ratios, connections, and locations",
            "Transformer connections and locations",
            "Protective relaying devices with exact relay types (27, 25, LBB, 27/59, 81U/O, 21-2/2, 51G, 51, 32, 25)",
            "Interrupting devices (fuse or circuit breaker not dependent on AC power)",
            "Voltage regulating devices between interconnection and utility substation",
            "Specific Notes: Trip times for relays",
            "Specific Notes: Typical relay functions and specific purposes (supervisory closing, fault tripping, synchronization)",
            "Specific Notes: Labels, notes, and distances related to hardware",
            "Specific Notes: CT/PT transformer types and connections (grounded-wye/grounded-wye, broken delta)",
            "Calculations: Relay product data sheets and proposed relay settings specified on RFRS",
            "Calculations: Demarcation drawings approved by customer and utility",
            "Calculations: Customer-provided relay settings and technical data for protection standards compliance",
          ],
          codeSection: "Relay Functional Requirements Specifications",
          source: "ComEd DER Interconnection Requirements, Relay Functional Requirements Specifications, Pages 42-50",
        },
        {
          label: "Interconnection - Submittal Documents",
          value: "",
          bullets: [
            "Illinois Commerce Commission Levels 2 to 4 Interconnection Application (83 Ill. Adm. Code 466, Appendix C)",
            "One-line diagram showing connections between generator(s) and the ComEd System (P.E. stamp required)",
            "Three-line diagrams showing current and potential circuits for protective relays (P.E. stamp required)",
            "Relay tripping and control schematic diagram (P.E. stamp required)",
            "Instruction books for relays on the RFRS forms",
            "Protective Relay Settings (P.E. stamp required)",
            "Site plan with POI indicated, access road location (if applicable), and array layout",
            "Equipment data sheets",
            "Proof of legal right to control the site — property tax bill in customer's name (if not currently a ComEd customer at the site)",
            "Proof of legal right to control the site — deed, lease agreement, option agreement, or other legally binding contract",
          ],
          codeSection: "Title 83, Part 466, Section 466.APPENDIX C",
          source: "Illinois Administrative Code; ComEd DER Interconnection Guidelines for Interconnection Customers",
        },
        {
          label: "Application Review Timeline",
          value: "",
          bullets: [
            "Level 1: 22 days",
            "Level 2: 60 days",
            "Level 3: 60 days",
            "Level 4: 205 days",
            "Pre-Application: 25 days",
            "Part 467 >10 MW: 275 days",
          ],
          codeSection: "Interconnection Process Timeline Appendix 1",
          source: "Commonwealth Edison (ComEd) Interconnection Process Timeline Appendix 1",
        },
      ],
    },
    {
      id: "municipal",
      number: "06",
      title: "Municipal Contact Information",
      icon: "landmark",
      dataPoints: [
        {
          label: "Building Department Address",
          value: "",
          bullets: ["4900 Village Commons", "Matteson", "IL 60443", "(708) 481-8313"],
          codeSection: "Community Development Department Building Services Division",
          source: "Community Development Department Building Services Division",
        },
        {
          label: "Fire Department Phone",
          value: "(708) 481-8313",
          codeSection: "Building Services Division contact information",
          source: "Community Development Department Building Services Division contact",
        },
        {
          label: "Permit Department Phone",
          value: "(708) 481-8313",
          codeSection: "Community Development Department Building Services Division contact details",
          source: "Community Development Department Building Services Division",
        },
      ],
    },
  ],
  footnotes: [
    "Village of Matteson Municipal Code, Section 150.001",
    "Village of Matteson Municipal Code, § 150.045 Adoption of Electrical Code",
    "Village of Matteson Code, Section 95.01",
    "Village of Matteson Zoning Code Ordinance, Sections 159.22, 159.05, 159.10, and 159.60",
    "Village of Matteson Zoning Code Ordinance, Sections 159.05, 159.10, and 159.22",
    "SB3409 - Public Act 102-1123, Section 5",
    "SB3409 - Public Act 102-1123",
    "Public Act 102-1123, HB4412 Enrolled, Section on Commercial Solar Energy Facility Setbacks",
    "Village of Matteson Zoning Code, Section related to Variances and Zoning Board of Appeals",
    "Renewable Energy Facilities Agricultural Impact Mitigation Act, Section 15(f); Village of Matteson Zoning Code Ordinance, Table 6; County Zoning Ordinance, Section on Energy Storage System Setbacks",
    "Renewable Energy Facilities Agricultural Impact Mitigation Act, Sections 10 and 15",
    "Renewable Energy Facilities Agricultural Impact Mitigation Act, 505 ILCS 147, Sections 10 and 15",
    "Renewable Energy Facilities Agricultural Impact Mitigation Act, Section 505 ILCS 147/10 and 147/15",
    "Community Development Department Building Services Division",
    "Village of Matteson Donation/Collection Container Permit Requirements Policy",
    "ComEd Distributed Energy Resource Interconnection Guidelines, Sections on Application Fees and Interconnection Levels",
    "Illinois Administrative Code Part 466; ComEd Distributed Energy Resource Interconnection Guidelines",
    "ComEd Distributed Energy Resource Interconnection Requirements, Relay Functional Requirements Specifications, Pages 42-50",
    "Illinois Administrative Code; ComEd DER Interconnection Guidelines for Interconnection Customers",
    "Village of Matteson Zoning Code Ordinance, Section 154.024 Building permits and certificates of occupancy",
    "Village of Matteson Zoning Code Ordinance, Institutional/Special Residence Permit Extension",
    "Matteson Construction Documentation Requirements; Village of Matteson Zoning and Building Code, Commercial Construction Drawing Standards",
    "Illinois Energy Efficient Building Code - 2018, Section 27.1.1.2; Village of Matteson Commercial/Industrial Checklist for Build Outs and Change of Use",
    "Illinois Energy Conservation Code, Section C103.1; Illinois Building Code, Section C103.1",
    "Illinois Public Act 102-1123",
    "Village of Matteson Residential Code Amendments, Table R301.2(1)",
    "Village of Matteson Code, Section 20-70.060; Village of Matteson Commercial/Industrial Checklist for Build Outs and Change of Use",
    "Commonwealth Edison (ComEd) Interconnection Process Timeline Appendix 1",
  ],
};

export const SOLAR_LANDSCAPE_REPORT: SampleReport = {
  slug: "solar-landscape",
  reportId: "SL-3A8F9C21",
  kicker: "Comprehensive Report",
  title: "Matteson, IL — 1.5 MW DC Solar + Storage",
  prospect: "Solar Landscape",
  prospectUrl: "https://www.solarlandscape.com/",
  jurisdiction: "Matteson, IL",
  county: "Cook County",
  city: "Matteson",
  projectType: "C&I",
  systemSize: "1.5 MW DC",
  utility: "Commonwealth Edison (ComEd)",
  reportRun: "Aug 17, 2026",
  verifiedLabel: "Aug 2026 · Seal of Authenticity",
  sections: [
    {
      id: "design",
      number: "01",
      title: "Design and Engineering",
      icon: "ruler",
      dataPoints: [
        {
          label: "Wind Speed",
          value: "115 mph",
          codeSection: "Table R301.2(1)",
          source: "Village of Matteson Residential Code Amendments, Table R301.2(1)",
        },
        {
          label: "Ground Snow Load",
          value: "30 psf",
          codeSection: "Table R301.2(1)",
          source: "Village of Matteson Residential Code, Table R301.2(1)",
        },
      ],
    },
    {
      id: "zoning",
      number: "02",
      title: "Zoning & Land Use",
      icon: "layers",
      dataPoints: [
        {
          label: "Solar Permissible Zone",
          value: "",
          bullets: [
            "Permitted by Right: Residential R-1 through R-7; Commercial C-1 through C-4; Mixed Use MXD; Industrial I-1 through I-3",
            "Conditional Approval: Commercial C-1 through C-5; Mixed Use MXD; Industrial I-1 through I-3",
            "Conditional Approval Process: Special Use Permit",
          ],
          codeSection: "Sections 159.22, 159.05, 159.10, and 159.60",
          source: "Village of Matteson Zoning Code Ordinance, Sections 159.22, 159.05, 159.10, and 159.60",
        },
        {
          label: "BESS Restricted Zones",
          value: "",
          bullets: [
            "Residential (unless special use permitted)",
            "Commercial (unless special use permitted)",
            "Mixed Use (unless special use permitted)",
          ],
          codeSection: "Sections 159.05, 159.10, and 159.22",
          source: "Village of Matteson Zoning Code Ordinance, Sections 159.05, 159.10, and 159.22",
        },
        {
          label: "Non-Residential BESS Setback from Non-Participating Property Line",
          value: "50 feet",
          codeSection: "Section 5; Section (f)",
          source: "SB3409 - Public Act 102-1123, Section 5",
        },
        {
          label: "Non-Residential BESS Setback from Non-Participating Occupied Structure",
          value: "150 feet",
          codeSection: "Section (f)",
          source: "SB3409 - Public Act 102-1123",
        },
        {
          label: "BESS Walkway Setback",
          value: "50 feet",
          source: "Illinois Public Act 102-1123",
        },
        {
          label: "Solar Property Line Requirements",
          value: "",
          bullets: [
            "Buffers: Vegetative screening may be required, no berms mandated",
            "Buffers: Pollinator-friendly vegetation and management plans may be required",
            "Fences: Perimeter fencing between 6 and 25 feet in height",
            "Panel Height Restrictions: Solar panels max height 20 feet at full tilt",
            "Sound: Sound limits match Illinois Pollution Control Board standards",
          ],
          codeSection: "Section (e), (l)",
          source: "SB3409 - Public Act 102-1123",
        },
        {
          label: "Solar Property Line Setback Distance",
          value: "50 feet",
          codeSection: "Section on Commercial Solar Energy Facility Setbacks",
          source: "Public Act 102-1123, HB4412 Enrolled",
        },
        {
          label: "Solar Property Line Variances",
          value: "Yes",
          codeSection: "Section related to Variances and Zoning Board of Appeals",
          source: "Village of Matteson Zoning Code",
        },
        {
          label: "Solar Road Setback",
          value: "50 feet",
          codeSection: "Section 15(f); Table 6",
          source:
            "Renewable Energy Facilities Agricultural Impact Mitigation Act, Section 15(f); Village of Matteson Zoning Code Ordinance, Table 6; County Zoning Ordinance, Section on Energy Storage System Setbacks",
        },
        {
          label: "Solar Decommissioning Financial Assurance",
          value: "",
          bullets: [
            "Application: Applies to commercial ground-mounted solar farms, including leased land",
            "Form: Must be a reclamation bond, surety bond, performance bond, letter of credit, escrow, or other acceptable financial assurance",
            "Amount: Max equals estimated decommissioning cost minus salvage value, phased payments 25%, 50%, and 100%",
            "Update Cadence: Updated every 5 years or less based on revised engineer estimates",
          ],
          codeSection: "Sections 10 and 15",
          source: "Renewable Energy Facilities Agricultural Impact Mitigation Act, Sections 10 and 15",
        },
        {
          label: "Solar Decommissioning Requirements",
          value: "",
          bullets: [
            "Equipment Removal: Remove all non-functional structures and debris",
            "Equipment Removal: Reuse or recycle equipment when commercially reasonable",
            "Equipment Removal: Complete decommissioning within 18 months of facility end",
            "Equipment Removal: Provide financial assurance for site restoration",
            "Site Restoration: Remove support structures and debris to 5 feet depth",
            "Site Restoration: Restore soil and vegetation close to preconstruction state",
            "Site Restoration: Repair agricultural drainage, compaction, rutting, erosion",
            "Site Restoration: Control weeds, clear trees, repair access roads",
            "Site Restoration: Maintain agricultural impact mitigation agreement on-site",
            "Soil Restoration: Remove and replace topsoil",
            "Soil Restoration: Repair compaction, rutting, and level land",
            "Soil Restoration: Prevent soil erosion and repair conservation practices",
            "Soil Restoration: Reroute and repair agricultural drainage tiles",
            "Soil Restoration: Remove rocks and control weeds",
          ],
          codeSection: "Sections 10 and 15",
          source: "Renewable Energy Facilities Agricultural Impact Mitigation Act, 505 ILCS 147, Sections 10 and 15",
        },
        {
          label: "Solar Decommissioning Timeline",
          value: "18 months",
          codeSection: "505 ILCS 147/10 and 147/15",
          source: "Renewable Energy Facilities Agricultural Impact Mitigation Act, 505 ILCS 147/10 and 147/15",
        },
      ],
    },
    {
      id: "code-cycle",
      number: "03",
      title: "Code Cycle",
      icon: "book",
      dataPoints: [
        {
          label: "Building Code Edition",
          value: "2018 International Building Code",
          codeSection: "Section 150.001",
          source: "Village of Matteson Municipal Code, Section 150.001",
        },
        {
          label: "Electrical Code Edition",
          value: "2017 National Electrical Code",
          codeSection: "§ 150.045 Adoption of Electrical Code",
          source: "Village of Matteson Municipal Code, § 150.045",
        },
        {
          label: "Fire Code Edition",
          value: "2018 International Fire Code",
          codeSection: "Section 95.01",
          source: "Village of Matteson Code, Section 95.01",
        },
      ],
    },
    {
      id: "permitting",
      number: "04",
      title: "Permitting",
      icon: "clipboard",
      dataPoints: [
        {
          label: "Permit Portal",
          value: "http://www.citizenserve.com/matteson",
          source: "Village of Matteson Donation/Collection Container Permit Requirements Policy",
        },
        {
          label: "Building Permit Expiration (months)",
          value: "6",
          codeSection: "Section 154.024",
          source: "Village of Matteson Zoning Code Ordinance, Section 154.024",
        },
        {
          label: "Building Permit Extension (months)",
          value: "24",
          codeSection: "Institutional/Special Residence Permit Extension provisions",
          source: "Village of Matteson Zoning Code Ordinance",
        },
        {
          label: "Drawing Standards",
          value: "",
          bullets: [
            'Sheet sizes: 24" x 36" full-sized working sheets for all design stages',
            "Ink color: No specific requirements",
            "Scale requirements: Drawings must be to scale; ratios not specified",
            "Title block info: Include vicinity map, scale, north arrow, engineer's title block",
            "Title block info continued: USGS benchmark, engineer seal, revision dates, project number",
            "Digital submissions: PDF allowed if approved by code official, sealed and stamped",
            "Layering: Start with cover sheet, include north arrow, seals, easements, spot elevations",
          ],
          codeSection: "Multiple sections as listed",
          source:
            "Matteson Construction Documentation Requirements; Village of Matteson Commercial Construction Drawing Standards; Sections 113.022 and R103",
        },
        {
          label: "File Specifications and Naming Conventions",
          value: "",
          bullets: [
            '24" x 36" sheet size for all design stages',
            "Minimum three sets of plans per permit application",
            "Electronic submissions only with approval; PDF format required",
            "Paper submissions bound in 3-ring binders, tabbed by section",
          ],
          codeSection: "Section 27.1.1.2; Section D Submittal Requirements; Section IV Engineering Plan Requirements",
          source:
            "Illinois Energy Efficient Building Code - 2018, Section 27.1.1.2; Village of Matteson Commercial/Industrial Checklist for Build Outs and Change of Use",
        },
        {
          label: "Professional Certification",
          value: "",
          bullets: [
            "Architect/engineer seal required on construction compliance materials",
            "Seals must comply with Illinois Architectural Practice and Engineering Acts",
            "Construction documents prepared by registered design professional",
            "Responsible professional info in title block: seal, signature, firm contact, project title",
          ],
          codeSection: "Section C103.1",
          source: "Illinois Energy Conservation Code, Section C103.1; Illinois Building Code, Section C103.1",
        },
        {
          label: "Demolition Requirements",
          value: "",
          bullets: [
            "Notifications: Code official orders demolition for unsafe structures, notices and postings required",
            "Escrow Requirements: No separate escrow; costs charged as liens if owner fails to comply",
            "Inspections: Required during and after demolition, 24-hour notice required",
            "Permits: Demolition permit required, contractors must be licensed, fees paid upfront, multiple department approvals needed",
            "Reporting: Salvage sale reports and test reports must be submitted and retained",
            "Failure to Comply: Demolition costs charged as lien if orders ignored",
            "Additional: Utility disconnection allowed for safety, hearing and appeal rights exist",
          ],
          source:
            "Village of Matteson Code, Section 20-70.060; Village of Matteson Commercial/Industrial Checklist for Build Outs and Change of Use",
        },
      ],
    },
    {
      id: "interconnection",
      number: "05",
      title: "Interconnection",
      icon: "plug",
      dataPoints: [
        {
          label: "Interconnection - Application Cost",
          value: "",
          bullets: [
            "Level 1: $50.00",
            "Level 2: $100.00 plus $1.00 per kVA",
            "Level 3: $500.00 plus $2.00 per kVA",
            "Level 4: $1,000.00 plus $2.00 per kVA",
          ],
          codeSection: "Sections on Application Fees and Interconnection Levels",
          source: "ComEd Distributed Energy Resource Interconnection Guidelines",
        },
        {
          label: "Interconnection - Application Level",
          value: "",
          bullets: [
            "Tier 1: 25 kW",
            "Tier 2: 5 MW",
            "Tier 3: 50 kW (Non-export systems)",
            "Tier 4: 10 MVA",
          ],
          codeSection: "Part 466, Section 466.80(b), 466.80(c)",
          source: "Illinois Administrative Code Part 466; ComEd DER Interconnection Guidelines",
        },
        {
          label: "Interconnection - Line Diagram Submittal Guidelines",
          value: "",
          bullets: [
            "Page Requirements: Completed Relay Functional Requirements Specifications (RFRS) form",
            "Electrical one-line diagram showing all equipment and connections",
            "Three-line diagrams for Level 3 interconnections",
            "Proposed CT/PT ratios, connections, and locations",
            "Transformer connections and locations",
            "Protective relaying devices with exact relay types (27, 25, LBB, 27/59, 81U/O, 21-2/2, 51G, 51, 32, 25)",
            "Interrupting devices (fuse or circuit breaker not dependent on AC power)",
            "Voltage regulating devices between interconnection and utility substation",
            "Specific Notes: Trip times for relays",
            "Specific Notes: Typical relay functions and specific purposes (supervisory closing, fault tripping, synchronization)",
            "Specific Notes: Labels, notes, and distances related to hardware",
            "Specific Notes: CT/PT transformer types and connections (grounded-wye/grounded-wye, broken delta)",
            "Calculations: Relay product data sheets and proposed relay settings specified on RFRS",
            "Calculations: Demarcation drawings approved by customer and utility",
            "Calculations: Customer-provided relay settings and technical data for protection standards compliance",
          ],
          codeSection: "Relay Functional Requirements Specifications",
          source: "ComEd DER Interconnection Requirements, Relay Functional Requirements Specifications, Pages 42-50",
        },
        {
          label: "Interconnection - Submittal Documents",
          value: "",
          bullets: [
            "Illinois Commerce Commission Levels 2 to 4 Interconnection Application (83 Ill. Adm. Code 466, Appendix C)",
            "One-line diagram showing connections between generator(s) and the ComEd System (P.E. stamp required)",
            "Three-line diagrams showing current and potential circuits for protective relays (P.E. stamp required)",
            "Relay tripping and control schematic diagram (P.E. stamp required)",
            "Instruction books for relays on the RFRS forms",
            "Protective Relay Settings (P.E. stamp required)",
            "Site plan with POI indicated, access road location (if applicable), and array layout",
            "Equipment data sheets",
            "Proof of legal right to control the site — property tax bill in customer's name (if not currently a ComEd customer at the site)",
            "Proof of legal right to control the site — deed, lease agreement, option agreement, or other legally binding contract",
          ],
          codeSection: "Title 83, Part 466, Section 466.APPENDIX C",
          source: "Illinois Administrative Code; ComEd DER Interconnection Guidelines for Interconnection Customers",
        },
        {
          label: "Application Review Timeline",
          value: "",
          bullets: [
            "Level 1: 22 days",
            "Level 2: 60 days",
            "Level 3: 60 days",
            "Level 4: 205 days",
            "Pre-Application: 25 days",
            "Part 467 >10 MW: 275 days",
          ],
          codeSection: "Interconnection Process Timeline Appendix 1",
          source: "Commonwealth Edison (ComEd) Interconnection Process Timeline Appendix 1",
        },
      ],
    },
    {
      id: "municipal",
      number: "06",
      title: "Municipal Contact Information",
      icon: "landmark",
      dataPoints: [
        {
          label: "Building Department Address",
          value: "",
          bullets: ["4900 Village Commons", "Matteson", "IL 60443", "(708) 481-8313"],
          codeSection: "Community Development Department Building Services Division",
          source: "Community Development Department Building Services Division",
        },
        {
          label: "Fire Department Phone",
          value: "(708) 481-8313",
          codeSection: "Building Services Division contact information",
          source: "Community Development Department Building Services Division contact",
        },
        {
          label: "Permit Department Phone",
          value: "(708) 481-8313",
          codeSection: "Community Development Department Building Services Division contact details",
          source: "Community Development Department Building Services Division",
        },
      ],
    },
  ],
  footnotes: [
    "Village of Matteson Municipal Code, Section 150.001",
    "Village of Matteson Municipal Code, § 150.045 Adoption of Electrical Code",
    "Village of Matteson Code, Section 95.01",
    "Village of Matteson Zoning Code Ordinance, Sections 159.22, 159.05, 159.10, and 159.60",
    "Village of Matteson Zoning Code Ordinance, Sections 159.05, 159.10, and 159.22",
    "SB3409 - Public Act 102-1123, Section 5",
    "SB3409 - Public Act 102-1123",
    "Public Act 102-1123, HB4412 Enrolled, Section on Commercial Solar Energy Facility Setbacks",
    "Village of Matteson Zoning Code, Section related to Variances and Zoning Board of Appeals",
    "Renewable Energy Facilities Agricultural Impact Mitigation Act, Section 15(f); Village of Matteson Zoning Code Ordinance, Table 6; County Zoning Ordinance, Section on Energy Storage System Setbacks",
    "Renewable Energy Facilities Agricultural Impact Mitigation Act, Sections 10 and 15",
    "Renewable Energy Facilities Agricultural Impact Mitigation Act, 505 ILCS 147, Sections 10 and 15",
    "Renewable Energy Facilities Agricultural Impact Mitigation Act, Section 505 ILCS 147/10 and 147/15",
    "Community Development Department Building Services Division",
    "Village of Matteson Donation/Collection Container Permit Requirements Policy",
    "ComEd Distributed Energy Resource Interconnection Guidelines, Sections on Application Fees and Interconnection Levels",
    "Illinois Administrative Code Part 466; ComEd Distributed Energy Resource Interconnection Guidelines",
    "ComEd Distributed Energy Resource Interconnection Requirements, Relay Functional Requirements Specifications, Pages 42-50",
    "Illinois Administrative Code; ComEd DER Interconnection Guidelines for Interconnection Customers",
    "Village of Matteson Zoning Code Ordinance, Section 154.024 Building permits and certificates of occupancy",
    "Village of Matteson Zoning Code Ordinance, Institutional/Special Residence Permit Extension",
    "Matteson Construction Documentation Requirements; Village of Matteson Zoning and Building Code, Commercial Construction Drawing Standards",
    "Illinois Energy Efficient Building Code - 2018, Section 27.1.1.2; Village of Matteson Commercial/Industrial Checklist for Build Outs and Change of Use",
    "Illinois Energy Conservation Code, Section C103.1; Illinois Building Code, Section C103.1",
    "Illinois Public Act 102-1123",
    "Village of Matteson Residential Code Amendments, Table R301.2(1)",
    "Village of Matteson Code, Section 20-70.060; Village of Matteson Commercial/Industrial Checklist for Build Outs and Change of Use",
    "Commonwealth Edison (ComEd) Interconnection Process Timeline Appendix 1",
  ],
};


export const SOLAR_OPTIMUM_REPORT: SampleReport = {
  slug: "solar-optimum",
  reportId: "CR-7B41C9A2",
  kicker: "Comprehensive Report",
  title: "Palo Alto, CA — Residential Solar + Storage",
  prospect: "Solar Optimum",
  prospectUrl: "https://solaroptimum.com/",
  jurisdiction: "Palo Alto, CA",
  county: "Santa Clara County",
  city: "Palo Alto",
  projectType: "Residential",
  systemSize: "Up to 30 kW",
  utility: "Pacific Gas & Electric (PG&E)",
  reportRun: "Aug 13, 2026",
  verifiedLabel: "Aug 2026 · Seal of Authenticity",
  sections: [
    {
      id: "design",
      number: "01",
      title: "Design and Engineering",
      icon: "ruler",
      dataPoints: [
        {
          label: "Solar Rooftop Fire Access Pathways",
          value: "",
          bullets: [
            "Distance to ridge (arrays ≤33% roof area): 18 inches",
            "Distance to ridge (arrays >33% roof area): 36 inches",
            "Eave to ridge path width: 36 inches",
            "Distance to skylights: minimal obstruction",
            "Non-habitable structures: exempt",
          ],
          codeSection: "Sections R329.6, R329.6.1, R329.6.2 and 1205.2.1, 1205.2.1.1, 1205.2.1.2",
          source:
            "California Residential Code, Sections R329.6, R329.6.1, R329.6.2 and California Fire Code, Sections 1205.2.1, 1205.2.1.1, 1205.2.1.2",
        },
        {
          label: "Solar Supply Side Connections Allowed - Utility",
          value: "Yes",
          codeSection: "Sections 705.12(B)(2)(3)(c), 705.10; Labeling Requirements; Electric Service Informational Note",
          source:
            "California Residential Energy Storage Systems and Photovoltaic Installations, Sections 705.12(B)(2)(3)(c), 705.10; Palo Alto Residential Energy Storage Systems, Labeling Requirements",
        },
        {
          label: "Placard Requirements",
          value: "",
          bullets: [
            "Permanent signage on meter panel indicating alternative generation source",
            '"UTILITY AC DISCONNECT SWITCH" placard on AC disconnect, red and white colors',
            "AC disconnect sign letters minimum 3/8 inch, all caps",
            "Disconnect signage visible, permanent, at 48–75 inches height",
            "Map/signage if disconnect not adjacent or >10 feet from meter panel",
            "Contact info signage if disconnect not accessible outside locked premises",
            "Engraved placard for line-side connections on metering equipment",
            "Weatherproof, UV-protected identification plates on meters and disconnects",
            "Maps/signage at each switchboard/meter stack in multi-meter setups",
            "Placards shown on single line diagrams submitted for interconnection",
          ],
          codeSection: "Section 5.5.1",
          source: "Pacific Gas and Electric Company Greenbook, Section 5.5.1",
        },
        {
          label: "Production Meter Required",
          value: "No",
          codeSection: "Production Meter Requirement Section; Form 79-1151A and Form 79-1193; Electric Rule 21, Section 5",
          source:
            "PG&E Net Energy Metering Interconnection for Solar and/or Wind Electric Generating Facilities of 30 Kilowatts or Less",
        },
        {
          label: "Solar Rapid Shut Down Visibility",
          value: "Required when placard doesn't exist",
          codeSection: "Article 690.12(C) and 690.12(D), Section 690.56(C), 1205.4.3, 1205.4(D)",
          source:
            "Residential Photovoltaic Systems Guidance (Palo Alto); 2025 California Electrical Code and 2025 California Fire Code",
        },
        {
          label: "Solar Utility Meter Collars Allowed",
          value: "No",
          codeSection: "Section 1a",
          source: "PG&E Transmission Interconnection Handbook, Section 1a",
        },
        {
          label: "Ground Snow Load",
          value: "0 psf",
          codeSection: "Section 1608A.2 Ground Snow Loads",
          source: "California Building Code, Section 1608A.2 and Figures 1608A.2(1)–(4)",
        },
        {
          label: "Wind Speed",
          value: "110 mph",
          codeSection: "Section 1609.3",
          source: "2025 California Building Code, Section 1609.3",
        },
        {
          label: "Solar AC Disconnecting Means Location",
          value:
            "An AC Disconnect is required within 10 feet and in line-of-sight of the main service panel; if not line-of-sight, a UV-resistant placard indicating its location is required.",
          codeSection: "Rule 27 Disconnect and Utility Requirements",
          source: "Residential Photovoltaic Systems, Utilities Rules and Regulations Rule 27",
        },
        {
          label: "Solar AC Disconnecting Means Required",
          value: "Yes",
          codeSection: "Section 690.13 and Utility Department Requirement; Table 5-6",
          source: "Residential Photovoltaic Systems, Section 690.13; PG&E Greenbook, Table 5-6",
        },
        {
          label: "BESS Heat Alarm Required",
          value: "Always",
          codeSection: "Sections R330.7, 1207.11.6, 1207.5.5, 903.3.1.1.5; 16.06.200 R314.1",
          source:
            "Palo Alto Residential Energy Storage Systems; California Residential Code and Fire Code; Palo Alto Municipal Code 16.06.200 R314.1",
        },
      ],
    },
    {
      id: "code-cycle",
      number: "02",
      title: "Code Cycle",
      icon: "book",
      dataPoints: [
        {
          label: "Building Code Edition",
          value: "2024 International Building Code",
          codeSection: "Sections 16.04.050 and 16.14; California Building Code, 2025",
          source: "Building Code of City of Palo Alto, Sections 16.04.050 and 16.14; California Building Code, 2025",
        },
        {
          label: "Electrical Code Edition",
          value: "2023 National Electrical Code",
          codeSection: "2025 California Electrical Code",
          source: "2025 California Electrical Code",
        },
        {
          label: "Fire Code Edition",
          value: "2024 International Fire Code",
          codeSection: "Section 1.1.1",
          source: "California Fire Code, Part 9 of California Code of Regulations, Title 24, Section 1.1.1",
        },
        {
          label: "Residential Code Edition",
          value: "2024 International Residential Code",
          codeSection: "California Residential Code, 2025 Edition",
          source: "California Residential Code, 2025 Edition",
        },
      ],
    },
    {
      id: "permitting",
      number: "03",
      title: "Permitting",
      icon: "clipboard",
      dataPoints: [
        {
          label: "Solar Expedited Permitting",
          value: "",
          bullets: [
            "Platform Name: SolarAPP+",
            "Applies to: Small residential rooftop solar only",
            "Max System Size: Up to 10 kW AC or 30 kW thermal",
            "Electrical Constraints: No electrical service upgrades allowed",
            "Structural Constraints: Must meet fire, structural, electrical codes",
            "Interconnection: City Utilities approval required before inspection",
            "Energy Storage: No expedited platform available",
            "EV Chargers: No expedited platform available",
          ],
          codeSection: "Chapter 16.63",
          source: "City of Palo Alto Municipal Code, Chapter 16.63; Residential Photovoltaic Systems permit guidelines",
        },
        {
          label: "Solar Permit Cost",
          value: "",
          bullets: ["Solar (≤10 kW): $1,481", "Solar (>10 kW): $1,868", "Storage: $1,859"],
          codeSection: "Building Permit Fees Section, pages 62-63",
          source: "City of Palo Alto Fiscal Year 2026 Adopted Municipal Fee Schedule",
        },
        {
          label: "Solar Professional Engineering Required",
          value: "Yes",
          codeSection: "Section CRC R324.4.1",
          source: "Residential Photovoltaic Systems Permit Process, Section CRC R324.4.1",
        },
        {
          label: "File Specifications and Naming Conventions",
          value: "",
          bullets: [
            "File Format: PDF",
            "File Naming: Use cycle number, address, and suffix (DOCS, PLAN, APPLY)",
            "File Quantity: 2 or 3 PDFs per review cycle",
            'Plan Sheet Size: Minimum 11"x17", maximum 30"x42"',
            "Submission Method: Electronically via Accela Citizen Access (ACA) system",
            "Text Size on Drawings: Minimum 0.12 inches height (10 pt font)",
          ],
          codeSection: "Submittal Instructions, File Naming and Drawing Specifications",
          source: "Palo Alto Building Permit Submittal Checklist and Plan Requirements",
        },
        {
          label: "Permit Portal",
          value: "https://aca-prod.accela.com/PALOALTO/Default.aspx",
          codeSection: "Online Permit Submittal Portal URL",
          source: "City of Palo Alto Building Permit Submittal Instructions",
        },
        {
          label: "Solar Site Plan",
          value: "",
          bullets: [
            "Roof plan showing vents, chimneys, skylights",
            "Roof slope and flow direction; roofing materials, gutters, overflow drains",
            "Show all buildings and structures on site, including accessory buildings",
            "Property line setback distances if equipment detached",
            "Indicate overhead or underground electrical service",
            "Location of proposed PV panels on roof",
            "Location and size (AMPS) of existing main electrical panel and meter",
            "Location and size of existing PV, Energy Storage, EV Charging",
            "Proposed equipment: panelboards, disconnects, inverters",
            'Location of PV system disconnect labeled "PV SYSTEM DISCONNECT"',
            "Conduit location from solar zone to electrical service",
            "Equipment in attic or crawlspace and access location",
            "Rapid shutdown zones on roof diagram if multiple types",
            "Signage and labeling including disconnect and rapid shutdown locations",
            "Structural attachment details and calculations",
            "Elevation plans if in floodplain; equipment at least 1 foot above Base Flood Elevation",
          ],
          codeSection: "Site Plan Requirements",
          source: "City of Palo Alto Residential Solar Submittal Checklist, Site Plan Sections",
        },
        {
          label: "Solar Structural Plan",
          value: "",
          bullets: [
            "Structural calculations for solar racking and equipment anchorage",
            "Existing roof framing plan with rafter size, spacing, bearing walls, collar ties",
            "Framing members shown on side views or roof cross sections",
            "Solar PV panel layout on roof; rack attachment and panel anchorage details",
            "Fasteners and anchorage methods for solar racks and panels",
            "Building section side views or roof cross sections",
            "Waterproofing details and roof penetrations",
            "Conduit routing and equipment placement notes",
            "Stick-framed roofs: framing plans, load calculations, single line diagrams, fire classification compliance, equipment spec sheets",
            "Pre-engineered trusses: truss design drawings per CRC Section R802.10, slope/depth/span/spacing, joint locations, bearing widths, loads",
            "Lumber specifications, connection details, reaction forces, deflection ratios, bracing locations and joint connector types",
          ],
          codeSection: "Structural Plans and Specifications",
          source: "City of Palo Alto Residential Solar Permit Requirements, Structural Plans section",
        },
        {
          label: "Solar Submittal Guidelines",
          value: "",
          bullets: [
            "PLAN file with site plan, electrical panels, ESS, floor plans, equipment, elevations, structural details, electrical diagrams, signage, tree protection",
            "DOCS file with manufacturer instructions, structural calculations, electrical load calc., interconnection agreement, load sheet",
            'Sheet/Page Size: Minimum 11" x 17", Maximum 30" x 42"',
            'Font/Text Size: Minimum size 10 font (~0.12" height)',
            "Drawing Scale: Clear and legible at specified sheet sizes",
            "Cover sheet with project info and cycle number",
            "Site plan with buildings and locations; roof plan showing roof-mounted equipment and PV system",
            "Floor plans showing roof access, electrical rooms, PV equipment",
            "Single line electrical diagram with PV and ESS switches",
            "Structural plans with roof framing and PV layout",
            "Signage package with load calculations and labeling",
          ],
          codeSection: "PLAN and DOCS submittal requirements and Plan Set",
          source: "City of Palo Alto Residential Solar Permit Requirements and Plan Set Formatting Guidelines",
        },
      ],
    },
    {
      id: "interconnection",
      number: "04",
      title: "Interconnection",
      icon: "plug",
      dataPoints: [
        {
          label: "Interconnection - Application Cost",
          value: "",
          bullets: [
            "Tier 1 (Fast Track): $0 for NEM-1, $145 for NEM-2 ≤ 1 MW",
            "Tier 2 (Study): $145 for NEM-2 ≤ 1 MW",
            "Tier 3 (Detailed Study): $800 for Non-NEM > 1 MW and NEM-2 > 1 MW",
          ],
          codeSection: "Section E.3",
          source: "Electric Rule No. 21 Generating Facility Interconnections, Section E",
        },
        {
          label: "Interconnection - Application Level",
          value: "",
          bullets: [
            "Systems 30 kW or less: Eligible for Standard Net Energy Metering",
            "Systems over 30 kW up to 1 MW: Subject to larger program processes",
            "Systems above 1 MW: Governed by different interconnection rules",
          ],
          codeSection: "Electric Rule 21",
          source: "PG&E Electric Rule 21; elec-forms-79-1151b-03; Interconnections & Renewables — PG&E",
        },
        {
          label: "Line Diagram Submittal Guidelines",
          value: "",
          bullets: [
            "Certification statements on SLD templates",
            "Statement on variance request requirement",
            "Confirmation on storage devices not exceeding peak demand",
            "Panel voltage (Volts); main breaker size (Amps); generator or storage breaker size (Amps)",
            "Service address and meter number",
            "Utility meter manufacturer and model; main breaker manufacturer and model",
            "AC Disconnect switch within 10 ft of PG&E meter, with manufacturer and amp rating",
            "CEC listed inverter with nameplate ratings; generation breaker details",
            "Battery storage DC and AC ratings; PV panel details",
            "Point of interconnection to PG&E system",
            "Customer load location relative to generation",
            "Power output connection and breaker ratings",
            "Backup controller make and model for backup systems",
            "Protective device ratings and manufacturers",
            "Basic SLD for PG&E required equipment; custom SLD for modifications or variance requests",
          ],
          codeSection: "Section 8 Electric Metering: Pedestals; Section E",
          source:
            "PG&E Transmission Interconnection Handbook, Section 8; PG&E Electric Rule No. 21, Section E Interconnection Request Submission Process",
        },
        {
          label: "Site Plan Submittal Guidelines",
          value: "",
          bullets: [
            "Photos of service panel and switchboard, marked for interconnection",
            "AC Disconnect Switch within 10 feet of PG&E meter, labeled and accessible; variance required if it exceeds 10 feet or multiple disconnects",
            "PV system mounting method",
            "Equipment details: manufacturer, model, rating, quantity",
          ],
          codeSection: "Section E. Interconnection Request Submission Process",
          source: "PG&E Electric Rule No. 21; Greenbook Manual, Section 8 Electric Metering: Pedestals",
        },
      ],
    },
    {
      id: "licensing",
      number: "05",
      title: "Licensing",
      icon: "badge",
      dataPoints: [
        {
          label: "Solar Contractor Licensing",
          value: "",
          bullets: ["C-46 Solar Contractor license", "Local business license required: No"],
          codeSection: "Residential Photovoltaic Systems; Business Registration Ordinance",
          source:
            "California Residential Solar Installation Licensing Requirements; City of Palo Alto Business Registration Ordinance",
        },
        {
          label: "Business License Portal",
          value: "https://paloalto.hdlgov.com/",
          codeSection: "Business License Renewal",
          source: "City of Palo Alto Business License Information",
        },
      ],
    },
    {
      id: "municipal-contact",
      number: "06",
      title: "Municipal Contact Information",
      icon: "landmark",
      dataPoints: [
        {
          label: "Building Department Address",
          value: "",
          bullets: ["285 Hamilton Ave., Palo Alto, CA 94301", "(650) 329-2496"],
          codeSection: "Building Department",
          source: "City of Palo Alto Building Department",
        },
        {
          label: "Building Department Official",
          value: "",
          bullets: [
            "Name: George Hoyt",
            "Title: Chief Building Official",
            "Email: George.Hoyt@paloalto.gov",
            "Phone: (650) 329-2368",
          ],
          codeSection: "Section Contact",
          source: "City of Palo Alto Development Services Building Division Contact Information",
        },
        {
          label: "Electrical Inspection Phone",
          value: "(650) 329-2496",
          codeSection: "Building Department contact information",
          source: "City of Palo Alto Inspection and Building Documents, General Contact Information",
        },
        {
          label: "Fire Department Phone",
          value: "(650) 329-2184, option 1",
          codeSection: "Fire Department Inspection Contact",
          source: "City of Palo Alto, Fire Department Inspection Contact",
        },
        {
          label: "Inspection Department Officials",
          value: "",
          bullets: [
            "Kyle Shea, Building Inspection Manager, Kyle.Shea@PaloAlto.gov, (650) 617-3507",
            "Daniel Mauldin, Senior Building Inspector, Daniel.Mauldin@PaloAlto.gov, (650) 329-2306",
            "Robert MacDonald, Building Inspector Specialist, Robert.MacDonald@PaloAlto.gov, (650) 329-2245",
            "Mark Hoadley, Building Inspector Specialist Electrical, Mark.Hoadley@PaloAlto.gov, (650) 329-2663",
            "James Yonathan, Building Inspector Specialist Architectural, James.Yonathan@PaloAlto.gov, (650) 329-2109",
            "Romany Youssef, Building Inspector, Romany.Youssef@PaloAlto.gov, (650) 617-3110",
            "Jake Wirt, Building Inspector, Jacob.Wirt@PaloAlto.gov, (650) 329-2535",
            "Forest Johnson, Building Inspector, Forest.Johnson@PaloAlto.gov, (650) 838-2753",
            "John Morton, Building Inspector, John.Morton@PaloAlto.gov, (650) 566-4526",
          ],
          codeSection: "Contact the Inspection Team",
          source: "Building Inspection — City of Palo Alto, CA, Contact the Inspection Team",
        },
        {
          label: "Permit Department Phone",
          value: "(650) 329-2496",
          codeSection: "General contact information",
          source: "City of Palo Alto Building Permits information",
        },
        {
          label: "Planning Department Official",
          value: "",
          bullets: [
            "Claire Raybould",
            "Manager of Current Planning",
            "Claire.Raybould@paloalto.gov",
            "(650) 329-2116",
          ],
          codeSection: "Planning & Development Services Managers",
          source: "Planning and Development Services Staff Directory — City of Palo Alto, CA",
        },
      ],
    },
    {
      id: "inspections",
      number: "07",
      title: "Inspections",
      icon: "search",
      dataPoints: [
        {
          label: "Electrical Inspection Method",
          value: "On-Site",
          codeSection: "Palo Alto Electrical Inspection Requirements",
          source: "City of Palo Alto Building Inspection and Electrical Inspection Procedures",
        },
        {
          label: "Electrical Inspection Scheduling Method",
          value: "Online Portal",
          codeSection: "Scheduling Electrical Inspections",
          source: "City of Palo Alto Inspection Scheduling Procedures",
        },
      ],
    },
  ],
  footnotes: [
    "Building Code of City of Palo Alto, Sections 16.04.050 and 16.14; California Building Code, 2025",
    "2025 California Electrical Code",
    "California Fire Code, Part 9 of California Code of Regulations, Title 24, Section 1.1.1",
    "California Residential Code, 2025 Edition",
    "City of Palo Alto Development Services Building Division Contact Information",
    "Building Inspection — City of Palo Alto, CA, Contact the Inspection Team",
    "California Residential Code, Sections R329.6, R329.6.1, R329.6.2 and California Fire Code, Sections 1205.2.1, 1205.2.1.1, 1205.2.1.2",
    "California Residential Energy Storage Systems and Photovoltaic Installations, Sections 705.12(B)(2)(3)(c), 705.10",
    "PG&E Greenbook Manual, Section 8 Electric Metering: Pedestals; PG&E Transmission Interconnection Handbook, Section L3.6",
    "Pacific Gas and Electric Company Greenbook, Section 5.5.1",
    "PG&E Net Energy Metering Interconnection for Facilities of 30 Kilowatts or Less",
    "Residential Photovoltaic Systems Guidance (Palo Alto); 2025 California Electrical Code, Article 690.12(C) and 690.12(D), Section 690.56(C); 2025 California Fire Code",
    "Electric Rule No. 21 Generating Facility Interconnections, Section E",
    "PG&E Electric Rule 21; elec-forms-79-1151b-03; Interconnections & Renewables — PG&E",
    "City of Palo Alto Municipal Code, Chapter 16.63; Residential Photovoltaic Systems permit guidelines",
    "City of Palo Alto Fiscal Year 2026 Adopted Municipal Fee Schedule",
    "Residential Photovoltaic Systems Permit Process, Section CRC R324.4.1",
    "PG&E Transmission Interconnection Handbook, Section 1a",
    "California Building Code, Section 1608A.2 and Figures 1608A.2(1) through 1608A.2(4)",
    "2025 California Building Code, Section 1609.3",
    "City of Palo Alto Building Inspection and Electrical Inspection Procedures",
    "City of Palo Alto Inspection Scheduling Procedures",
    "Palo Alto Building Permit Submittal Checklist and Plan Requirements",
    "City of Palo Alto Building Permit Submittal Instructions",
    "City of Palo Alto Residential Solar Submittal Checklist, Site Plan Sections",
    "City of Palo Alto Residential Solar Permit Requirements, Structural Plans section",
    "City of Palo Alto Residential Solar Permit Requirements and Plan Set Formatting Guidelines",
    "Residential Photovoltaic Systems, Utilities Rules and Regulations Rule 27",
    "Residential Photovoltaic Systems, Section 690.13 and Utility Department Requirement",
    "PG&E Greenbook Manual, Table 5-6",
    "California Residential Solar Installation Licensing Requirements; City of Palo Alto Business Registration Ordinance",
    "Palo Alto Residential Energy Storage Systems, Sections R330.7, 1207.11.6, 1207.5.5, 903.3.1.1.5; Palo Alto Municipal Code 16.06.200 R314.1",
    "City of Palo Alto Business License Information",
  ],
};

export const PERMIT_POWER_REPORT: SampleReport = {
  ...SOLAR_OPTIMUM_REPORT,
  slug: "permit-power",
  reportId: "PP-8B3D6E51",
  prospect: "Permit Power",
  prospectUrl: "https://www.permitpower.com/",
  reportRun: "Sep 23, 2026",
  verifiedLabel: "Sep 2026 · Seal of Authenticity",
};

export const SIMPLY_SOLAR_REPORT: SampleReport = {
  slug: "simply-solar",
  reportId: "SS-7E4B2D9F",
  kicker: "Feasibility Report",
  title: "Concord (Residential)",
  prospect: "Simply Solar",
  prospectUrl: "https://simplysolar.com/",
  jurisdiction: "Concord, CA",
  county: "Contra Costa County",
  city: "Concord",
  projectType: "Residential",
  systemSize: "7.4 kW",
  utility: "Pacific Gas & Electric (PG&E)",
  reportRun: "Aug 20, 2026",
  verifiedLabel: "Aug 2026 · Seal of Authenticity",
  sections: [
    {
      id: "design",
      number: "01",
      title: "Design and Engineering",
      icon: "ruler",
      dataPoints: [
        {
          label: "Solar Rooftop Fire Access Pathways",
          value: "",
          bullets: [
            "Distance to ridge (arrays ≤33% roof area): 18 inches",
            "Distance to ridge (arrays >33% roof area): 36 inches",
            "Eave to ridge path width: 36 inches",
            "Distance to skylights: 36 inches",
            "Distance to primary street-facing eave: 36 inches",
            "Distance to secondary street-facing eave: 36 inches",
          ],
          codeSection: "Sections 1205.2.1, R329.6, R329.7",
          source:
            "Concord California Residential Solar Photovoltaic (PV) Permit Guidance; 2025 California Fire Code; California Residential Code",
        },
        {
          label: "Solar Supply Side Connections Allowed - Utility",
          value: "Yes",
          codeSection: "Section 230.82(6); Section L3.6",
          source:
            "2025 California Electrical Code Amendments, Articles 706.16, 705.12; PG&E Greenbook Section 8 and Transmission Interconnection Handbook, Section L3.6",
        },
        {
          label: "Placard Requirements - Utility",
          value: "",
          bullets: [
            "Permanent signage on meter panel indicating alternative generation source",
            '"UTILITY AC DISCONNECT SWITCH" placard on AC disconnect, red and white colors',
            "AC disconnect sign letters minimum 3/8 inch, all caps",
            "Disconnect signage visible, permanent, at 48-75 inches height",
            "Map/signage if disconnect not adjacent or >10 feet from meter panel",
            "Contact info signage if disconnect not accessible outside locked premises",
            "Engraved placard for line-side connections on metering equipment",
            "Weatherproof, UV-protected identification plates on meters and disconnects",
            "Maps/signage at each switchboard/meter stack in multi-meter setups",
            "Placards shown on single line diagrams submitted for interconnection",
          ],
          codeSection: "Section 5.5.1",
          source: "Pacific Gas and Electric Company Greenbook, Section 5.5.1",
        },
        {
          label: "Production Meter Required - Utility",
          value: "Conditional",
          codeSection:
            "Production Meter Requirement Section; Form 79-1151A and Form 79-1193; Electric Rule 21, Section 5",
          source:
            "PG&E Net Energy Metering (NEM) Interconnection for Solar and/or Wind Electric Generating Facilities of 30 Kilowatts or Less",
        },
        {
          label: "Production Meter Requirements - Utility",
          value: "",
          bullets: [
            "Production or REC Meter required for residential solar systems up to 30 kW",
            "One Production Meter per system under 25 kW",
            "Required under NEM schedule NEM2 for systems under 25 kW",
            "PG&E furnishes meter equipment for systems under 25 kW",
            "Customer must wire meter socket without test bypass facilities",
          ],
          codeSection:
            "Part II - NEM Generator System Size; Section 1. METERING; Sections 5 & 8 Electric Metering",
          source:
            "ELEC_FORMS_79-1193; ELEC_SCHEDS_NEM2, Section 1. METERING; greenbook-manual-full, Sections 5 & 8",
        },
        {
          label: "Solar Rapid Shut Down Visibility",
          value: "Required when placard doesn't exist",
          codeSection:
            "2025 Building Energy Efficiency Standards; Section 690.12; Section 89.111",
          source:
            "2025 Building Energy Efficiency Standards, City of Concord Guidance; California Electrical Code, Section 690.12; California Fire Code, Section 89.111",
        },
        {
          label: "Solar Max AC System Size",
          value: "30 kW",
          codeSection: "Part II - NEM Generator System Size",
          source:
            "Pacific Gas & Electric (PG&E), AGREEMENT AND CUSTOMER AUTHORIZATION Net Energy Metering Interconnection For Solar And/Or Wind Electric Generating Facilities Of 30 Kilowatts Or Less",
        },
        {
          label: "Solar Utility Meter Collars Allowed",
          value: "No",
          codeSection: "Section 1a",
          source:
            "PG&E Transmission Interconnection Handbook, Section 1a. Generator Breaker or Contactor at the Interconnection",
        },
        {
          label: "Ground Snow Load",
          value: "0 psf",
          codeSection: "ASCE 7-22",
          source: "ASCE 7-22",
        },
        {
          label: "Wind Speed",
          value: "95 mph",
          codeSection: "Section 1609.3",
          source: "California Building Code - 2025, Section 1609.3",
        },
        {
          label: "Solar AC Disconnecting Means Location",
          value:
            "An AC disconnect is required and must be installed at a readily accessible location near the PV system connection, within 10 feet and line-of-sight, or locking provisions apply. A placard identifying the disconnect location is also required.",
          codeSection: "Sections 690.13, 690.15, 690.56(B)",
          source: "California Electrical Code, Sections 690.13, 690.15, 690.56(B)",
        },
        {
          label: "Solar AC Disconnecting Means Required - Utility",
          value: "Yes",
          codeSection: "Section 690.13; Table 5-6",
          source:
            "California Electrical Code - 2025, Section 690.13; greenbook-manual-full, Table 5-6",
        },
        {
          label: "BESS Heat Alarm Required",
          value: "Always",
          codeSection: "Section 1207.11.6 and Section R330.7",
          source: "California Electrical Code, Section 1207.11.6 and Section R330.7",
        },
      ],
    },
    {
      id: "code-cycle",
      number: "02",
      title: "Code Cycle",
      icon: "book",
      dataPoints: [
        {
          label: "Building Code Edition",
          value: "2024 International Building Code",
          codeSection: "Section 89.102.1; Section 1.1.1; Section 1.1.11",
          source:
            "California Building Standards Code; California Residential Code; California Building Code",
        },
        {
          label: "Electrical Code Edition",
          value: "2023 National Electrical Code",
          codeSection: "2025 California Electrical Code (Title 24, Part 3)",
          source: "2025 California Electrical Code (Title 24, Part 3)",
        },
        {
          label: "Fire Code Edition",
          value: "2024 International Fire Code",
          codeSection: "2025 California Fire Code (CFC) (Title 24, Part 9)",
          source: "2025 California Fire Code (CFC) (Title 24, Part 9)",
        },
        {
          label: "Residential Code Edition",
          value: "2024 International Residential Code",
          codeSection: "Section 1.1 General",
          source: "California Residential Code - 2025",
        },
      ],
    },
    {
      id: "permitting",
      number: "03",
      title: "Permitting",
      icon: "clipboard",
      dataPoints: [
        {
          label: "Solar Expedited Permitting",
          value: "",
          bullets: [
            "Expedited Platform: SolarAPP+",
            "Applies to: Solar; Solar + Storage",
            "Does not apply to: EV chargers",
          ],
          codeSection: "SolarAPP+ Expedited Permitting for Residential Solar PV",
          source: "Solar PV Projects | Concord, CA",
        },
        {
          label: "Solar Permit Cost",
          value: "",
          bullets: [
            "Solar: $70 administrative fee; $115 plan review for systems up to 15 kW, plus $5 per kW above 15 kW; inspection base fee $265 + $10 per kW above 15 kW",
            "SolarAPP: $70 administrative fee up to 38.4 kW max; inspection base fee $380 + $15 per kW above 15 kW",
            "Storage: No separate permit fee listed",
            "Main Service Panel Upgrade inspection fee: $192",
          ],
          codeSection: "Fees and Charges for Various Municipal Services",
          source: "Exhibit A. Resolution 78 6042",
        },
        {
          label: "Solar Professional Engineering Required",
          value: "Required if system weight exceeds 5 psf",
          codeSection: "Signatures - When required plans shall be signed",
          source: "Residential Solar PV Information Bulletin",
        },
        {
          label: "Building Permit Expiration",
          value: "36 months",
          codeSection: "Building Permit Renewal and Completing Work on Canceled Permits",
          source: "Building Permit Renewals and Completing Work on a Canceled Permit",
        },
        {
          label: "Building Permit Extension",
          value: "12 months",
          codeSection: "Extension of Applications or Permits Prior to Expiration",
          source: "Building Permit Renewals and Completing Work on a Canceled Permit",
        },
        {
          label: "Building Permit Submittal Method, Solar",
          value: "Online Portal",
          codeSection: "Section I. PERMIT APPROVAL REQUIREMENTS",
          source:
            "Residential Solar PV Information Bulletin; Solar PV Projects | Concord, CA, SUBMITTAL REQUIREMENTS",
        },
        {
          label: "Permit Portal",
          value: "https://aca-prod.accela.com/CONCORD/Default.aspx",
        },
        {
          label: "Solar Site Plan",
          value: "",
          bullets: [
            "Project name and address on each sheet; statement of code compliance",
            "Location of the property and street address; assessor's parcel number",
            "Name, address, and phone number of property owner, contractor, and designer(s)",
            "License numbers and engineer/architect/designer info",
            "Accurate boundary line survey to scale showing lot lines",
            "Size and location of new and existing structures; distances from lot lines",
            "General roof configuration including hips and valleys",
            "PV array location and layout, showing module count and layout",
            "Number of strings and modules per string",
            "Existing roof features like skylights, vents, plumbing vents",
            "Roof access points, pathways, and solar installation setbacks",
            "Framing layout and sizing of existing roof framing",
            "Manufacturer installation specs if pre-manufactured racking used",
            "Roofing material type, sheathing thickness, number of layers",
            "Conduit runs shown on roof plan (non-electrical details)",
            "Locations of inverters, combiners, disconnects, and main service",
            "Mounting bracket type, size, spacing, and fastener location",
            "Details for load distribution, structural integrity, and weatherproofing",
          ],
          codeSection: "Standard Plan templates (PV-1 through PV-4)",
          source: "Residential Solar PV Information Bulletin",
        },
        {
          label: "Solar Structural Plan",
          value: "",
          bullets: [
            "Side view showing framing member size, spacing, type, and span of rafters or trusses",
            "Framing reinforcement details if required",
            "Mounting details with fastener size, spacing, type, roof penetrations, weatherproofing",
            "PV panel height above roof surface (max 30 inches flat, 18 inches pitched)",
            "Roofing materials details: type, roof sheathing thickness, number of layers",
            "Structural calculations if system load exceeds 5 psf, attachment loads exceed 50 lbs, or modifications",
            "Structural Information Worksheet (WKS-1) demonstrating code compliance",
            "Project name, address, designer signature on all sheets",
            "Bearing walls, collar ties, or other supports on side view",
            "Roofing material layers with thickness on side view",
          ],
          codeSection: "Sections 2 and 5",
          source: "Residential Solar PV Information Bulletin",
        },
        {
          label: "Solar Submittal Guidelines",
          value: "",
          bullets: [
            "Manufacturer data sheets for all major equipment",
            "Systems permit application and submittal package",
            "Solar permitting checklist via Virtual Permit Center",
            "Sheet/page size: standard letter or suitable PDF size",
            "Font/text size: minimum 3/8 inch letter height, Arial or similar",
            "Drawing scale: minimum 1/8\" = 1'-0\" for all plans",
            "Cover Sheet (PV-1) with project and code information",
            "Site address, project name, designer's signature on each sheet",
            "Roof plan showing PV array layout, module specs, equipment enclosures",
            "Mounting details including fasteners and roof structure",
            "Single line electrical diagram from meter to PV array",
            "Signage and labeling per NEC Article 690 and UL 969",
            "Equipment info: manufacturer, busbar, OCPD ratings",
            "Conductor wire types and sizes for all circuits",
            "Grounding electrode conductor and electrode details",
            "Notes on conduit/support spacing per code",
            "Permitting and inspection checklist fields",
          ],
          codeSection: "SUBMITTAL REQUIREMENTS",
          source: "Solar PV Projects - Concord, CA",
        },
      ],
    },
    {
      id: "interconnection",
      number: "04",
      title: "Interconnection",
      icon: "plug",
      dataPoints: [
        {
          label: "Application Cost",
          value: "",
          bullets: [
            "Tier 1 (Fast Track): $0 for NEM-1, $145 for NEM-2 ≤ 1 MW",
            "Tier 2 (Study): $145 for NEM-2 ≤ 1 MW",
            "Tier 3 (Detailed Study): $800 for Non-NEM > 1 MW and NEM-2 > 1 MW",
          ],
          codeSection: "Section E.3",
          source: "Electric Rule No. 21 Generating Facility Interconnections, Section E",
        },
        {
          label: "Application Level",
          value: "",
          bullets: [
            "Systems 30 kW or less: Eligible for Standard Net Energy Metering",
            "Systems over 30 kW up to 1 MW: Subject to larger program processes",
            "Systems above 1 MW: Governed by different interconnection rules",
          ],
          codeSection: "Electric Rule 21; elec-forms-79-1151b-03",
          source: "PG&E Electric Rule 21; Interconnections & Renewables | PG&E",
        },
        {
          label: "Excess Energy Export Credits",
          value: "Rate per kWh of the customer's Otherwise Applicable Rate Schedule",
          codeSection: "Section 2. NET ENERGY METERING AND BILLING",
          source: "ELEC_SCHEDS_NEM2, Section 2. NET ENERGY METERING AND BILLING",
        },
        {
          label: "Line Diagram Submittal Guidelines",
          value: "",
          bullets: [
            "Certification statements on SLD templates",
            "Statement on variance request requirement",
            "Confirmation on storage devices not exceeding peak demand",
            "Panel voltage (Volts); main breaker size (Amps)",
            "Generator or storage breaker size (Amps)",
            "Service address and meter number",
            "Utility meter manufacturer and model",
            "Main breaker manufacturer and model",
            "AC Disconnect switch within 10 ft of PG&E meter",
            "CEC listed inverter with nameplate ratings",
            "Generation breaker details",
            "Battery storage DC and AC ratings; PV panel details",
            "Point of interconnection to PG&E system",
            "Customer load location relative to generation",
            "Power output connection and breaker ratings",
            "Backup controller make and model for backup systems",
            "Protective device ratings and manufacturers",
            "AC Disconnect switch manufacturer and amp rating",
            "Basic SLD for PG&E required equipment; custom SLD for modifications or variance requests",
          ],
          codeSection: "Section 8 ELECTRIC METERING: PEDESTALS",
          source:
            "PG&E Transmission Interconnection Handbook; PG&E Electric Rule No. 21, Section E; greenbook-manual-full",
        },
        {
          label: "Site Plan Submittal Guidelines",
          value: "",
          bullets: [
            "Photos of service panel and switchboard, marked for interconnection",
            "AC Disconnect Switch within 10 feet of PG&E meter, labeled and accessible; variance required if AC Disconnect exceeds 10 feet or multiple disconnects",
            "PV system mounting method",
            "Equipment details: manufacturer, model, rating, quantity",
          ],
          codeSection: "Section E. INTERCONNECTION REQUEST SUBMISSION PROCESS",
          source:
            "PG&E Electric Rule No. 21, Section E; Greenbook Manual, Section 8 ELECTRIC METERING: PEDESTALS",
        },
        {
          label: "Submittal Documents",
          value: "",
          bullets: [
            "Net Energy Metering or Net Billing Tariff Interconnection Agreement and Customer Authorization",
            "Completed Interconnection Application",
            "Final signed jurisdiction building permit",
            "Manufacturer's spec sheets for AC Disconnect device (if applicable)",
            "Documentation of Site Exclusivity (except certain Generating Facilities)",
            "Single-Line Diagram (Basic or Custom)",
          ],
          codeSection: "Section E.2 Interconnection Request Process",
          source: "Elec Forms 79-1151b-03; Rule 21",
        },
      ],
    },
    {
      id: "licensing",
      number: "05",
      title: "Licensing",
      icon: "badge",
      dataPoints: [
        {
          label: "Solar Contractor Licensing",
          value: "",
          bullets: [
            "C-46 Solar Contractor license",
            "C-10 Electrical Contractor license",
            "Local Business License: Yes",
          ],
          codeSection: "Signatures - When required plans shall be signed",
          source: "Residential Solar PV Information Bulletin",
        },
        {
          label: "Business License Portal",
          value: "https://concord.hdlgov.com/Home/Index/BusinessLicense",
          codeSection: "ONLINE BUSINESS LICENSE SERVICES",
          source: "City of Concord Business License Information",
        },
      ],
    },
    {
      id: "contacts",
      number: "06",
      title: "Municipal Contact Information",
      icon: "landmark",
      dataPoints: [
        {
          label: "Building Department Address",
          value: "",
          bullets: ["1950 Parkside Drive MS/51", "Concord, CA 94519", "(925) 671-3107"],
          source: "City of Concord | Community Development Department | Building Division",
        },
        {
          label: "Building Department Hours of Operation",
          value: "Monday – Friday: 7:30 AM – 6:00 PM",
          codeSection: "Work Hours",
          source: "Pre-Construction Meeting, Work Hours",
        },
        {
          label: "Building Department Official",
          value: "",
          bullets: [
            "Title: Building Official",
            "Email: bldgpermits@cityofconcord.org",
            "Phone Number: 925-671-3107",
            "Office Location: 1950 Parkside Drive, MS/51, Concord, CA 94519",
          ],
          codeSection: "Contact Information",
          source: "City of Concord Building Division, Contact Information",
        },
        {
          label: "Electrical Inspection Phone",
          value: "(925) 671-3330",
          codeSection: "City Contact Numbers; Building Division Contact",
          source: "City of Concord Building Division, Contact Numbers",
        },
        {
          label: "Fire Department Phone",
          value: "(925) 941-3300",
          codeSection: "Concord City Contact Numbers section",
          source: "LOCAL REGULATIONS — Concord, City Contact Numbers",
        },
        {
          label: "Inspection Department Officials",
          value: "",
          bullets: [
            "Chris Vieira — Building Inspector/Electrical — cvieira@ci.concord.ca.us",
            "Larry Baltazor — Building Inspector — larrybaltazo@ci.concord.ca.us",
            "Tony Anton — Building Inspector — tanton@ci.concord.ca.us",
            "Ron Riesselmen — Building Inspector — rriesselmen@ci.concord.ca.us",
            "Marie Flores — Building Inspector — mflores@ci.concord.ca.us",
          ],
          codeSection: "Building Inspectors",
          source: "Concord, City Contact Numbers",
        },
        {
          label: "Permit Department Phone",
          value: "(925) 671-3107",
          codeSection: "Building Division contact information",
          source: "City of Concord Building Division, Contact Information",
        },
        {
          label: "Planning Department Official",
          value: "",
          bullets: [
            "Name: Aaron Sage",
            "Title: Planning Manager",
            "Phone: (925) 671-3465",
            "Email: Aaron.Sage@cityofconcord.org",
          ],
        },
      ],
    },
    {
      id: "inspections",
      number: "07",
      title: "Inspections",
      icon: "search",
      dataPoints: [
        {
          label: "Electrical Inspection Method",
          value: "On-Site",
          codeSection: "Electrical inspection requirements",
          source:
            "Building Inspection Requirements and Procedures; Concord Permit Process, Electrical Inspections",
        },
        {
          label: "Electrical Inspection Scheduling Method",
          value: "Online Portal",
          codeSection: "Inspections Requests",
          source: "City of Concord Building Inspection Procedures, Section: Inspections Requests",
        },
      ],
    },
  ],
  footnotes: [
    "Concord California Residential Solar Photovoltaic (PV) Permit Guidance; City of Concord Community and Economic Development",
    "Residential Solar PV Information Bulletin; Solar PV Projects | Concord, CA",
    "Exhibit A. Resolution 78 6042 — Fees and Charges for Various Municipal Services",
    "Building Permit Renewals and Completing Work on a Canceled Permit",
    "2025 California Fire Code, California Residential Code, California Building Code, California Electrical Code",
    "PG&E Greenbook Manual; PG&E Transmission Interconnection Handbook",
    "PG&E Electric Rule No. 21 Generating Facility Interconnections; ELEC_SCHEDS_NEM2; ELEC_FORMS_79-1193",
    "ASCE 7-22",
    "City of Concord Business License Information",
  ],
};

export const VERDE_REPORT: SampleReport = {
  ...PIVOT_REPORT,
  slug: "verde-solutions",
  reportId: "VS-20B4C7A1",
  prospect: "Verde Solutions",
  prospectUrl: "https://verdesolutions.com/",
  reportRun: "Aug 21, 2026",
};

export const SUNVEST_REPORT: SampleReport = {
  ...SOLAR_LANDSCAPE_REPORT,
  slug: "sunvest-solar",
  reportId: "SV-7B2E4D91",
  prospect: "SunVest Solar",
  prospectUrl: "https://www.sunvestsolar.com/",
  reportRun: "Aug 28, 2026",
};

export const DSD_RENEWABLES_REPORT: SampleReport = {
  ...SOLAR_LANDSCAPE_REPORT,
  slug: "dsd-renewables",
  reportId: "DR-5C1A8B32",
  prospect: "DSD Renewables",
  prospectUrl: "https://www.dsdrenewables.com/",
  reportRun: "Sep 1, 2026",
};

export const POWERFLEX_REPORT: SampleReport = {
  ...SOLAR_LANDSCAPE_REPORT,
  slug: "powerflex",
  reportId: "PF-9D3E7F12",
  prospect: "Powerflex",
  prospectUrl: "https://www.powerflex.com/",
  reportRun: "Sep 2, 2026",
};

export const ONYX_RENEWABLES_REPORT: SampleReport = {
  ...SOLAR_LANDSCAPE_REPORT,
  slug: "onyx-renewables",
  reportId: "OR-3F8A9B21",
  prospect: "Onyx Renewables",
  prospectUrl: "https://www.onyxrenewables.com/",
  reportRun: "Sep 1, 2026",
};

export const MARS_ENERGY_GROUP_REPORT: SampleReport = {
  ...SOLAR_LANDSCAPE_REPORT,
  slug: "mars-energy-group",
  reportId: "ME-4A7B8C32",
  prospect: "Mars Energy Group",
  prospectUrl: "https://www.marsenergygroup.com/",
  reportRun: "Sep 1, 2026",
};


export const PARADISE_ENERGY_SOLUTIONS_REPORT: SampleReport = {
  slug: "paradise-energy-solutions",
  reportId: "PE-5D2E8F14",
  kicker: "Feasibility Report",
  title: "Rockville, MD (Residential)",
  prospect: "Paradise Energy Solutions",
  prospectUrl: "https://www.paradiseenergysolutions.com/",
  jurisdiction: "Rockville, MD",
  county: "Montgomery County",
  city: "Rockville",
  projectType: "Residential",
  systemSize: "8 kW DC",
  utility: "Pepco",
  reportRun: "Sep 1, 2026",
  verifiedLabel: "Sep 2026 · Seal of Authenticity",
  sections: [
    {
      id: "design",
      number: "01",
      title: "Design and Engineering",
      icon: "ruler",
      dataPoints: [
        {
          label: "Solar Rooftop Fire Access Pathways",
          value: "",
          bullets: [
            "Distance to ridge (arrays ≤33% roof area): 18 inches",
            "Distance to ridge (arrays >33% roof area): 36 inches with sprinklers up to 66% coverage; 36 inches over 66% coverage",
            "Eave to ridge path width: 36 inches per side (total 72 inches)",
            "Distance to skylights: 36 inches",
            "Distance to primary street-facing eave: 36 inches pathways (minimum two, on separate planes, one on street side)",
            "Distance to secondary street-facing eave: 18 inches up to 33% coverage, 36 inches over 33% coverage; 36 inches with sprinklers up to 66% coverage, 36 inches over 66% coverage",
          ],
          codeSection: "Sections 1205.2.1.1, 1205.2.1.2, 1205.2.1.3; R324.6.1; R324.6.2; R324.6.2.1; R324.6.3",
          source: "Rockville, MD Code of Ordinances, Chapter 9 - FIRE CODE; Sections 1205.2.1.1, 1205.2.1.2, 1205.2.1.3; IRC Sections R324.6.1, R324.6.2, R324.6.2.1, R324.6.3",
        },
        {
          label: "Solar Supply Side Connections Allowed",
          value: "Yes",
          codeSection: "Article 705.12",
          source: "NFPA-70-National-Electrical-Code-2023, Article 705.12",
        },
        {
          label: "Solar Supply Side Connections Allowed",
          value: "Yes",
          codeSection: "Sections 4.3 Battery Storage; 10.9.2 Sub-Station Power Transformers; Transformer requirements for DER >= 250 KVA",
          source: "PHI DER Technical Interconnection Requirements, Sections 4.3, 10.9.2, and relevant transformer requirements for DER >= 250 KVA",
        },
        {
          label: "Placard Requirements",
          value: "",
          bullets: [
            "\"Solar PV AC Disconnect\" placard, red color, on exterior AC disconnect",
            "\"Caution, Solar Electric System\" placard, yellow color, on meter housing",
            "Meter placard must indicate disconnect location if disconnect is away",
            "\"Rapid Shutdown Equipment\" placard with red border, white background",
            "\"PV Rapid Shutdown\" emergency responder placard",
            "Emergency Contact placard for battery systems",
            "Fire Safety Warning placard for energy storage batteries",
          ],
          codeSection: "Section FACILITY INFORMATION; Page 25",
          source: "Pepco_MD_Level_1_-_Interconnection_Application-Agreement_blank_2018Dec, Section FACILITY INFORMATION; Pepco_MD_Level_234_-_Interconnection_Application-Agreement_blank_2019April, Page 25",
        },
        {
          label: "Production Meter Required",
          value: "Conditional: Required for systems ≥ 25kW",
          codeSection: "Electric Service Handbook 2018 - Chapter 4, PHI DER Technical Interconnection Requirements, COMAR 20.50.10",
          source: "Electric Service Handbook 2018, Chapter 4 - Customers' Installations, Renewable Small Generation section; PHI DER Technical Interconnection Requirements; Code of Maryland Regulations 20.50.10 - Net Metering",
        },
        {
          label: "Production Meter Requirements",
          value: "",
          bullets: [
            "Production or REC Meter not required for systems under 25 kW",
            "Net-capable energy meter with reverse registration required",
            "Typically, only one production meter supplied and maintained by PEPCO",
            "Metering provided at no direct charge unless larger meter requested",
            "Line side wiring connects to top terminals of meter socket",
            "Load side wiring connects to bottom terminals of meter socket",
            "Isolating switch with visible break required on line side in some cases",
            "Approved Meter Socket Adapters allowed but do not replace net meter",
          ],
          codeSection: "Sections G and H under NET ENERGY METERING RIDER \"NEM\"; COMAR 20.50.10.01 and 20.50.10.02; COMAR 20.50.09.02, 20.50.09.08, 20.50.09.10; Section 13; Chapter 5 Section D",
          source: "2025-11-26_MASTER_MD_Pepco_Tariff_Book_pdf, NET ENERGY METERING RIDER \"NEM",
        },
        {
          label: "Solar AC Disconnecting Means Location",
          value: "The AC Disconnect must be in an accessible location outside or within 5 feet inside from service conductors entrance, not in bathrooms, within line of sight of service disconnect, inverter, and meter. If not in line of sight, placards indicating location are required.",
          codeSection: "Articles 690, 110.21(B), Section 1207.4.1; Rockville Code Chapter 5 Technical Standards Section 230.70; Sections 1205.4.1.2, 1205.4.3",
          source: "NFPA-70-National-Electrical-Code-2023 (NEC), Articles 690, 110.21(B) and Section 1207.4.1; Rockville, MD Code of Ordinances, Chapter 5 Technical Standards, Section 230.70; SECTION 1205 SOLAR PHOTOVOLTAIC POWER SYSTEMS, Sections 1205.4.1.2, 1205.4.3",
        },
        {
          label: "Solar AC Disconnecting Means Required - Utility",
          value: "Yes",
          codeSection: "690.13 Photovoltaic System Disconnecting Means",
          source: "NFPA-70-National-Electrical-Code-2023 (NEC), Section 690.13",
        },
        {
          label: "Solar AC Disconnecting Means Required",
          value: "Yes",
          codeSection: "PHI DER Technical Interconnection Requirements, Section 5.8",
          source: "PHI DER Technical Interconnection Requirements, Section 5.8 - Isolation Device; COMAR §20.50.09.06 - General Requirements",
        },
        {
          label: "BESS Heat Alarm Required",
          value: "Not Required if Sprinklers Exist",
          codeSection: "Section 1207.11.6 and Section 903.3.1.1",
          source: "Ordinance-17-25-To-Amend-Chapter-9-of-the-Rockville-City-Code-to-Adopt-the-2024-Editions-of-the-Fire-Code-NFPA-1-and-the-Life-Safety-Code-NFPA-2024, Section 1207.11.6 and Section 903.3.1.1",
        },
      ],
    },
    {
      id: "code-cycle",
      number: "02",
      title: "Code Cycle",
      icon: "book",
      dataPoints: [
        {
          label: "Building Code Edition",
          value: "2021 International Building Code",
          codeSection: "Sections 5-86, 5-286, 5-221, 5-101, 5-161, 5-321",
          source: "Rockville, MD Code of Ordinances, Sections 5-86, 5-286, 5-221, 5-101, 5-161, 5-321",
        },
        {
          label: "Electrical Code Edition",
          value: "2023 National Electrical Code",
          codeSection: "Sec. 5-131",
          source: "Sec. 5-131. - National Electrical Code-Adopted, Rockville, MD Code of Ordinances",
        },
        {
          label: "Fire Code Edition",
          value: "2024 NFPA 1 Fire Code",
          codeSection: "Section 9-161",
          source: "Ordinance-17-25-To-Amend-Chapter-9-of-the-Rockville-City-Code-to-Adopt-the-2024-Editions-of-the-Fire-Code-NFPA-1-and-the-Life-Safety-Code-NFPA-2024, Section 9-161",
        },
        {
          label: "Residential Code Edition",
          value: "2021 International Residential Code for One- and Two-Family Dwellings",
          codeSection: "Sec. 5-101",
          source: "Rockville, MD Code of Ordinances, Section 5-101",
        },
      ],
    },
    {
      id: "permitting",
      number: "03",
      title: "Permitting",
      icon: "clipboard",
      dataPoints: [
        {
          label: "Solar Expedited Permitting",
          value: "SolarAPP+",
          source: "Apply for an Automated Residential Solar Permit _ City of Rockville",
        },
        {
          label: "Solar Permit Cost",
          value: "$125",
          source: "Rockville MD FY27-ISD Fee Schedule",
        },
        {
          label: "Solar Professional Engineering Required",
          value: "Conditional: Required if array exceeds weight threshold, or if structural upgrades are performed to support PV loads.",
          codeSection: "Sections 5-86, 5-133; Residential-Building-Permit-Submittal-Requirements; Sections 5-66 through 5-87; Sections 1205.2.3, R905.17, R324.6; Section 1607.14.4.1",
          source: "Rockville, MD Code of Ordinances, Sections 5-86, 5-133; Residential-Building-Permit-Submittal-Requirements; City of Rockville Building Code, Sections 5-66 through 5-87; Rockville Local Building Code, Sections 1205.2.3, R905.17, R324.6; Residential Code, Section 1607.14.4.1",
        },
        {
          label: "Building Permit Expiration",
          value: "12",
          codeSection: "Sec. 25.07.08. - Special exceptions.",
          source: "Rockville Municipal Code, Sec.25.07.08 and related provisions",
        },
        {
          label: "Building Permit Extension",
          value: "12",
          codeSection: "n.Implementation period.",
          source: "Rockville, MD Code of Ordinances Sec. 25.07.08. - Special exceptions.",
        },
        {
          label: "Building Permit Submittal Method, Solar",
          value: "Online Portal",
          codeSection: "multiple sections",
          source: "Apply for an Automated Residential Solar Permit _ City of Rockville",
        },
        {
          label: "File Specifications and Naming Conventions",
          value: "",
          bullets: [
            "File Name: [HOUSE NUMBER] [STREET NAME] [FILE TYPE] - e.g., 123 Test St Checklist.pdf",
            "File Type: PDF only",
            "Page Sizes: minimum 11 x 17 inches",
            "Drawing Scale: minimum 1/4 inch = 1 foot (1/4\" = 1'-0\")",
            "Drawing Quality: digitally created or scanned clearly, no dark backgrounds or blurred images",
            "Quantity of Plans: two or more sets for permits",
            "Upload Requirements: Combine multiple pages into one file",
            "File Upload: electronic submission through MyGovernmentOnline (MGO) Customer Portal",
          ],
          codeSection: "Various sections including Residential Plan Submittal Requirements Architectural header; Article I, Section 5-12 of Rockville City Code; DPS guidelines",
          source: "Rockville Residential Plan Submittal Requirements; Rockville Building Design Checklist and Other Permitting Documents; Montgomery County Plan Submittal Guidelines; IgCC Amendments for Rockville; Rockville City Code Section 5-12; Residential Plan Submittal Requirements Document; New Single-Family Dwelling or Townhouse Permit Documents",
        },
        {
          label: "Permit Portal",
          value: "www.rockvillemd.gov/mgoconnect",
          codeSection: "Header 2: ALL applications are submitted online via MGO Connect",
          source: "Express-Accessory-Structure-Guide-Structures-Less-than-200-Feet, Header 2: ALL applications are submitted online via MGO Connect",
        },
        {
          label: "Solar Site Plan",
          value: "",
          bullets: [
            "Page Requirements:",
            "Drawn to scale",
            "North arrow",
            "Date",
            "Building/Site Identification:",
            "Setbacks to property lines",
            "Lot dimensions",
            "Dimensions and locations of drives, walks, fences, porches, patios, accessory buildings, planting areas, and freestanding signs",
            "Accurate location and dimensions of all improvements",
            "Solar Installation Details:",
            "Location of proposed solar structure, including roof arrays",
            "Firefighter access pathways, minimum 36 inches wide",
            "Ridge setbacks between 18 to 36 inches depending on array size",
            "Hardware Identification:",
            "Location of inverter",
            "Conduit routing pathways to electrical service panel",
            "Location of heat detectors for Battery Energy Storage Systems when applicable",
            "Architectural Details:",
            "Elevations, sections, and details as appropriate",
          ],
          codeSection: "Section 1205: Solar Photovoltaic Power Systems",
          source: "Residential Plan Submittal Requirements",
        },
        {
          label: "Solar Structural Plan",
          value: "",
          bullets: [
            "Calculations:",
            "Structural loads: snow, wind, design load criteria",
            "Unbalanced fill calculations",
            "Soil-bearing capacity in psf",
            "Compressive design strength for concrete elements",
            "Reinforcement steel placement details",
            "Truss design load calculations and bracing",
            "Structural Members:",
            "Structural columns and beams sizes and loads",
            "Joist and rafter sizes and spans",
            "Lateral blocking and framing details",
            "Bearing walls and collar ties",
            "Beam sizes with supporting calculations",
            "Building Sections and Cross-Sections:",
            "Dimensioned side view sections showing wall, floor, roof heights",
            "Footing depths and details",
            "Detailed roof cross-sections showing solar installation",
            "Solar Mounting Hardware:",
            "Dead load indications of PV panels and rack systems",
            "Solar rack attachments and connector details",
            "Truss connector types, locations, and permanent bracing",
            "Photovoltaic System Details:",
            "Roof plan view diagrams showing PV energized areas",
            "Permanent rapid shutdown and switch labels",
            "Distinctions for Roof Types:",
            "Stick-framed roofs: include lateral blocking and framing specifics",
            "Pre-engineered trussed roofs: provide sealed truss design drawings",
            "General Requirements:",
            "Plans drawn to scale and dimensioned",
            "Seal of licensed Structural Engineer or registered design professional",
          ],
          codeSection: "Structural; Sections 5-86 and 5-87; R802.10; R502.11.4; 1607.14.4; 1205.4.2; N104.1.2",
          source: "Residential-Building-Permit-Submittal-Requirements, Structural; City of Rockville Building Code, Sections 5-86 and 5-87; 2021 IRC, Sections R802.10, R502.11.4, 1607.14.4; SECTION 1205 SOLAR PHOTOVOLTAIC POWER SYSTEMS 1205.4.2; N104.1.2 Structural Plans, Specifications, and Engineering Details",
        },
        {
          label: "Solar Submittal Guidelines",
          value: "",
          bullets: [
            "Fully completed and signed Maryland Interconnection Application & Agreement",
            "Customer of record contact information",
            "Alternate contact information",
            "Facility information including address and Pepco account",
            "Type of application (new, addition, or upgrade)",
            "DC and AC inverter rating in kW",
            "Generator and inverter manufacturer name and model number",
            "Equipment installation contractor information",
            "Electrical contractor information",
            "Customer's signature",
            "Application fee (no fee for Level 1: 10 kW or less)",
            "Certificate of Completion",
            "Inspection Certificate with system type",
            "Single-line drawing of the system",
            "Site map",
          ],
          codeSection: "Level 1 Interconnection Application & Agreement Details, Parts I and II; Application Forms and Fees",
          source: "GPC_Pepco_MD_ApplicationChecklist_2015_01_28.pdf, Sections: Level 1 Interconnection Application & Agreement Details, Parts I and II, Application Forms and Fees",
        },
      ],
    },
    {
      id: "interconnection",
      number: "04",
      title: "Interconnection",
      icon: "plug",
      dataPoints: [
        {
          label: "Application Cost",
          value: "",
          bullets: [
            "Level 1: No fee",
            "Level 2: $50 plus $1 per kW inverter rating",
            "Level 3: $100 plus $2 per kW inverter rating",
            "Level 4: $100 plus $2 per kW inverter rating",
          ],
          codeSection: "Application Forms and Fees",
          source: "Pepco Maryland Interconnection Application & Agreement, Application Forms and Fees",
        },
        {
          label: "Application Level",
          value: "",
          bullets: [
            "Level 1: 10 kW or less",
            "Level 2: 2 MW or less",
            "Level 3: 50kW Area Network; 10MW Radial Distribution Circuit",
            "Level 4: up to 10 MW",
          ],
          codeSection: "Application Forms and Fees",
          source: "Pepco Holdings, Application Forms and Fees",
        },
        {
          label: "Excess Energy Export Credits",
          value: "$0.18354 per kWh (credit)",
          codeSection: "SCHEDULE \"PIV\"",
          source: "PEPCO Rate Schedules for Electric Services in Maryland",
        },
        {
          label: "Submittal Documents",
          value: "",
          bullets: [
            "Fully completed and signed Maryland Interconnection Application & Agreement",
            "Application fee (if applicable)",
            "Single-line drawing and site map",
            "Certificate of Completion",
            "Signed electric inspection form",
            "Interconnection customer contact information",
            "Existing utility account information",
            "Intended use of small generator facility",
            "Requested review level",
            "Technical info on generator and components",
            "Disconnect switch location and details",
            "Local or municipal electric code official approval",
          ],
          codeSection: "Section 20. Appendix F: Commissioning Checklist",
          source: "GPC Pepco Application Checklist; Pepco MD Level 1 - Interconnection Application-Agreement; PHI DER Technical Interconnection Requirements",
        },
      ],
    },
    {
      id: "licensing",
      number: "05",
      title: "Licensing",
      icon: "badge",
      dataPoints: [
        {
          label: "Solar Contractor Licensing",
          value: "",
          bullets: [
            "MHIC Contractor License",
            "Maryland State Contractor License",
            "Local Business License: No",
          ],
          codeSection: "Residential Solar Contractor Licensing Sections in City of Rockville and Montgomery County COMCOR 22.00.02.04",
          source: "City of Rockville Automated Residential Solar Permit documentation; Montgomery County COMCOR 22.00.02.04 Fire Protection System License",
        },
        {
          label: "Business License Portal",
          value: "https://www.dllr.state.md.us/license/elec/",
          source: "Residential Solar Panel Application",
        },
      ],
    },
    {
      id: "municipal",
      number: "06",
      title: "Municipal Contact Information",
      icon: "landmark",
      dataPoints: [
        {
          label: "Building Department Address",
          value: "",
          bullets: [
            "Street Name and Number: 111 Maryland Ave.",
            "City: Rockville",
            "State: MD",
            "Zip Code: 20850",
            "Phone Number: 240-314-8240",
          ],
          codeSection: "Building Department Contact Information",
          source: "Montgomery County Department of Permitting Services documents",
        },
        {
          label: "Building Department Hours of Operation",
          value: "",
          bullets: [
            "Monday: 8:30 a.m. - 5:00 p.m.",
            "Tuesday: 8:30 a.m. - 5:00 p.m.",
            "Wednesday: 8:30 a.m. - 5:00 p.m.",
            "Thursday: 8:30 a.m. - 5:00 p.m.",
            "Friday: 8:30 a.m. - 5:00 p.m.",
          ],
          codeSection: "Guidelines for Building Residential Decks - Section C; Express Permit Program _ City of Rockville.pdf sections 'Login During Express Permit Program Hours' and 'Select Express Permit Application'",
          source: "Monday & Thursday: Guidelines for Building Residential Decks, Section C. Where to Apply for Permits; Tuesday & Friday: Express Permit Program _ City of Rockville.pdf, sections 'Login During Express Permit Program Hours' and 'Select Express Permit Application'",
        },
        {
          label: "Building Department Official",
          value: "",
          bullets: [
            "Name: Evan Johnson",
            "Title: Director of Community Planning and Development Services",
            "Phone: 240-314-8200",
            "Email: cpds@rockvillemd.gov",
          ],
          source: "Rockville Building Department, Contact Information",
        },
        {
          label: "Electrical Inspection Phone",
          value: "(240) 314-8240",
          codeSection: "Inspection Services Division",
          source: "City of Rockville Inspection Services Division",
        },
        {
          label: "Fire Department Phone",
          value: "(240) 314-8240",
          codeSection: "Inspection Services Division",
          source: "Fire-Protection-Systems-Inspection-Procedures, Inspection Services Division",
        },
        {
          label: "Inspection Department Officials",
          value: "",
          bullets: [
            "Angel Maldonado, Senior Codes Inspector",
            "James Metzgar, Senior Plans Examiner",
            "Volodumyr Borus, Codes Inspector",
            "Terrence Dickens, Senior Codes Inspector",
            "Paul Kern, Codes Inspector",
            "William Gressen, Senior Codes Inspector",
            "Jeremy McDougal, Codes Inspector",
            "Kesha Marshall, Codes Inspector",
            "Chris Dempwolf, Codes Inspection Manager",
          ],
          codeSection: "Article V, Sections 5-66 through 5-87; 2026-CPDS-Org-Chart-R022626",
          source: "Rockville City Code, Article V, Sections 5-66 through 5-87; 2026-CPDS-Org-Chart-R022626; Single-Family-Sign-Posting-Procedures-3-28-2025",
        },
        {
          label: "Permit Department Phone",
          value: "(240) 314-8240",
          codeSection: "Permits and Inspections Contact Information",
          source: "City of Rockville, Maryland, Community Planning and Development Services",
        },
        {
          label: "Planning Department Official",
          value: "",
          bullets: [
            "Name: Nicole Walters",
            "Title: Planner II",
            "Email: nwalters@rockvillemd.gov",
            "Phone: 240-314-8215",
          ],
          codeSection: "Contact",
          source: "Express Permit Program _ City of Rockville, Contact",
        },
      ],
    },
    {
      id: "inspections",
      number: "07",
      title: "Inspections",
      icon: "search",
      dataPoints: [
        {
          label: "Electrical Inspection Method",
          value: "On-Site",
          codeSection: "Sections 19-7; 5-122; [A] 110 & [A] 109 Inspections",
          source: "Rockville, MD Code of Ordinances, Sections 19-7; 5-122; [A] 110 & [A] 109 Inspections",
        },
        {
          label: "Electrical Inspection Scheduling Method",
          value: "Online Portal",
          codeSection: "Online Scheduling Information; Fire-Protection-Systems-Inspection-Procedures",
          source: "City of Rockville Permit and Inspection Procedures, Online Scheduling Information; City of Rockville Inspection Procedures, Fire-Protection-Systems-Inspection-Procedures",
        },
      ],
    },
  ],
  footnotes: [
    "Rockville, MD Code of Ordinances, Chapter 9 - FIRE CODE; Sections 1205.2.1.1, 1205.2.1.2, 1205.2.1.3; IRC Sections R324.6.1, R324.6.2, R324.6.2.1, R324.6.3",
    "NFPA-70-National-Electrical-Code-2023, Article 705.12",
    "PHI DER Technical Interconnection Requirements, Sections 4.3, 10.9.2, and relevant transformer requirements for DER >= 250 KVA",
    "Pepco_MD_Level_1_-_Interconnection_Application-Agreement_blank_2018Dec, Section FACILITY INFORMATION; Pepco_MD_Level_234_-_Interconnection_Application-Agreement_blank_2019April, Page 25",
    "Electric Service Handbook 2018, Chapter 4 - Customers' Installations, Renewable Small Generation section; PHI DER Technical Interconnection Requirements; Code of Maryland Regulations 20.50.10 - Net Metering",
    "2025-11-26_MASTER_MD_Pepco_Tariff_Book_pdf, NET ENERGY METERING RIDER \"NEM",
    "NFPA-70-National-Electrical-Code-2023 (NEC), Articles 690, 110.21(B) and Section 1207.4.1; Rockville, MD Code of Ordinances, Chapter 5 Technical Standards, Section 230.70; SECTION 1205 SOLAR PHOTOVOLTAIC POWER SYSTEMS, Sections 1205.4.1.2, 1205.4.3",
    "NFPA-70-National-Electrical-Code-2023 (NEC), Section 690.13",
    "PHI DER Technical Interconnection Requirements, Section 5.8 - Isolation Device; COMAR §20.50.09.06 - General Requirements",
    "Ordinance-17-25-To-Amend-Chapter-9-of-the-Rockville-City-Code-to-Adopt-the-2024-Editions-of-the-Fire-Code-NFPA-1-and-the-Life-Safety-Code-NFPA-2024, Section 1207.11.6 and Section 903.3.1.1",
    "Rockville, MD Code of Ordinances, Sections 5-86, 5-286, 5-221, 5-101, 5-161, 5-321",
    "Sec. 5-131. - National Electrical Code-Adopted, Rockville, MD Code of Ordinances",
    "Ordinance-17-25-To-Amend-Chapter-9-of-the-Rockville-City-Code-to-Adopt-the-2024-Editions-of-the-Fire-Code-NFPA-1-and-the-Life-Safety-Code-NFPA-2024, Section 9-161",
    "Rockville, MD Code of Ordinances, Section 5-101",
    "Apply for an Automated Residential Solar Permit _ City of Rockville",
    "Rockville MD FY27-ISD Fee Schedule",
    "Rockville, MD Code of Ordinances, Sections 5-86, 5-133; Residential-Building-Permit-Submittal-Requirements; City of Rockville Building Code, Sections 5-66 through 5-87; Rockville Local Building Code, Sections 1205.2.3, R905.17, R324.6; Residential Code, Section 1607.14.4.1",
    "Rockville Municipal Code, Sec.25.07.08 and related provisions",
    "Rockville, MD Code of Ordinances Sec. 25.07.08. - Special exceptions.",
    "Rockville Residential Plan Submittal Requirements; Rockville Building Design Checklist and Other Permitting Documents; Montgomery County Plan Submittal Guidelines; IgCC Amendments for Rockville; Rockville City Code Section 5-12; Residential Plan Submittal Requirements Document; New Single-Family Dwelling or Townhouse Permit Documents",
    "Express-Accessory-Structure-Guide-Structures-Less-than-200-Feet, Header 2: ALL applications are submitted online via MGO Connect",
    "Residential Plan Submittal Requirements",
    "Residential-Building-Permit-Submittal-Requirements, Structural; City of Rockville Building Code, Sections 5-86 and 5-87; 2021 IRC, Sections R802.10, R502.11.4, 1607.14.4; SECTION 1205 SOLAR PHOTOVOLTAIC POWER SYSTEMS 1205.4.2; N104.1.2 Structural Plans, Specifications, and Engineering Details",
    "GPC_Pepco_MD_ApplicationChecklist_2015_01_28.pdf, Sections: Level 1 Interconnection Application & Agreement Details, Parts I and II, Application Forms and Fees",
    "Pepco Maryland Interconnection Application & Agreement, Application Forms and Fees",
    "Pepco Holdings, Application Forms and Fees",
    "PEPCO Rate Schedules for Electric Services in Maryland",
    "GPC Pepco Application Checklist; Pepco MD Level 1 - Interconnection Application-Agreement; PHI DER Technical Interconnection Requirements",
    "City of Rockville Automated Residential Solar Permit documentation; Montgomery County COMCOR 22.00.02.04 Fire Protection System License",
    "Residential Solar Panel Application",
    "Montgomery County Department of Permitting Services documents",
    "Monday & Thursday: Guidelines for Building Residential Decks, Section C. Where to Apply for Permits; Tuesday & Friday: Express Permit Program _ City of Rockville.pdf, sections 'Login During Express Permit Program Hours' and 'Select Express Permit Application'",
    "Rockville Building Department, Contact Information",
    "City of Rockville Inspection Services Division",
    "Fire-Protection-Systems-Inspection-Procedures, Inspection Services Division",
    "Rockville City Code, Article V, Sections 5-66 through 5-87; 2026-CPDS-Org-Chart-R022626; Single-Family-Sign-Posting-Procedures-3-28-2025",
    "City of Rockville, Maryland, Community Planning and Development Services",
    "Express Permit Program _ City of Rockville, Contact",
    "Rockville, MD Code of Ordinances, Sections 19-7; 5-122; [A] 110 & [A] 109 Inspections",
    "City of Rockville Permit and Inspection Procedures, Online Scheduling Information; City of Rockville Inspection Procedures, Fire-Protection-Systems-Inspection-Procedures",
  ],
};

export const PARADISE_ENERGY_SOLUTIONS_COMMERCIAL_REPORT: SampleReport = {
  slug: "paradise-energy-solutions-commercial",
  reportId: "PE-9C4A7B21",
  kicker: "Feasibility Report",
  title: "Rockville, MD (Commercial)",
  prospect: "Paradise Energy Solutions",
  prospectUrl: "https://www.paradiseenergysolutions.com/",
  jurisdiction: "Rockville, MD",
  county: "Montgomery County",
  city: "Rockville",
  projectType: "Commercial",
  systemSize: "1 MW AC",
  utility: "Pepco",
  reportRun: "Sep 1, 2026",
  verifiedLabel: "Sep 2026 · Seal of Authenticity",
  sections: [
    {
      id: "design",
      number: "01",
      title: "Design and Engineering",
      icon: "ruler",
      dataPoints: [
        {
          label: "Wind Speed",
          value: "115 mph",
          codeSection: "2308.2.4",
          source: "Maryland Building Code - 2021, Section 2308.2.4 Basic Wind Speed",
        },
        {
          label: "Ground Snow Load",
          value: "30 psf",
          source: "StructuralDesignLoadMinimumRequiredDataList",
        },
      ],
    },
    {
      id: "zoning",
      number: "02",
      title: "Zoning & Land Use",
      icon: "layers",
      dataPoints: [
        {
          label: "Solar Property Line Requirements",
          value: "",
          bullets: [
            "Buffers: Landscape buffers up to 35 feet with multilayered trees and shrubs required",
            "Fences: Non-barbed wire fences up to 20 feet high inside or adjacent to buffers",
            "Roads: No solar arrays or equipment allowed within public road rights-of-way except interconnection",
          ],
          codeSection: "Sections (15) and (16)",
          source: "Maryland Code, Public Utilities, Sections (15) and (16)",
        },
        {
          label: "Solar Property Line Variances",
          value: "No",
          codeSection: "Setback Requirements for Solar Energy Generating Stations; Article 25.06.03; Article 25.09.05",
          source: "City of Rockville Zoning Code and Maryland State Solar Energy Regulations; City of Rockville Zoning Ordinance, Article 25.06.03; Article 25.09.05",
        },
        {
          label: "Solar Road Setback",
          value: "50 feet",
          codeSection: "Section on Setbacks for Solar Energy Generating Stations; Section 25.12.04.a",
          source: "Maryland State Regulations on Solar Energy Generating Stations, Section on Setbacks for Solar Energy Generating Stations; City of Rockville Zoning Code, Section 25.12.04.a",
        },
        {
          label: "Solar Decommissioning Financial Assurance",
          value: "",
          bullets: [
            "Application: Must enter decommissioning agreement at end of commercial lifespan",
            "Form: Required to post a surety bond",
            "Amount: Surety bond up to 125% of estimated decommissioning cost minus salvage value",
            "Update Cadence: Bond true-up every 5 years",
          ],
          codeSection: "Section 7-218(g)",
          source: "Maryland Public Utilities Article, Section 7-218(g)",
        },
        {
          label: "Solar Decommissioning Requirements",
          value: "",
          bullets: [
            "Equipment Removal: Owner must post a surety bond of up to 125% of estimated decommissioning cost",
            "Equipment Removal: Submit a decommissioning agreement to the Public Service Commission",
            "Equipment Removal: Execute a securitization bond true-up every 5 years",
            "Site Restoration: Minimize grading as much as possible",
            "Site Restoration: Avoid removal of topsoil; topsoil can be stockpiled temporarily",
            "Site Restoration: Maintain soil integrity with native/noninvasive vegetation, 90% survival rate",
            "Site Restoration: Limit mowing and unnecessary landscaping",
            "Site Restoration: Use herbicides only for invasive species control",
            "Site Restoration: Maintain existing forest or hedgerows for visual screening, add new plantings as needed",
            "Soil Restoration: No topsoil removal; topsoil can be temporarily stockpiled",
            "Soil Restoration: Plant native or noninvasive vegetation with 90% survival rate",
            "Soil Restoration: Limit mowing and unnecessary landscaping",
            "Soil Restoration: Use herbicides only as per Dept. of Agriculture regulations",
            "Soil Restoration: Post a landscaping bond equal to 100% of landscaping costs for 5 years",
          ],
          codeSection: "Section §7-218 (f) and (g)",
          source: "Maryland Public Service Commission Regulations, Section §7-218 (f) and (g)",
        },
      ],
    },
    {
      id: "code-cycle",
      number: "03",
      title: "Code Cycle",
      icon: "book",
      dataPoints: [
        {
          label: "Building Code Edition",
          value: "2021 International Building Code",
          codeSection: "Sections 5-86, 5-286, 5-221, 5-101, 5-161, 5-321",
          source: "Rockville, MD Code of Ordinances, Sections 5-86, 5-286, 5-221, 5-101, 5-161, 5-321",
        },
        {
          label: "Electrical Code Edition",
          value: "2023 National Electrical Code",
          codeSection: "Sec. 5-131",
          source: "Sec. 5-131. - National Electrical Code-Adopted, Rockville, MD Code of Ordinances",
        },
        {
          label: "Fire Code Edition",
          value: "2024 NFPA 1 Fire Code",
          codeSection: "Section 9-161",
          source: "Ordinance-17-25-To-Amend-Chapter-9-of-the-Rockville-City-Code-to-Adopt-the-2024-Editions-of-the-Fire-Code-NFPA-1-and-the-Life-Safety-Code-NFPA-2024, Section 9-161",
        },
      ],
    },
    {
      id: "permitting",
      number: "04",
      title: "Permitting",
      icon: "clipboard",
      dataPoints: [
        {
          label: "Permit Portal",
          value: "www.rockvillemd.gov/mgoconnect",
          codeSection: "Header 2: ALL applications are submitted online via MGO Connect",
          source: "Express-Accessory-Structure-Guide-Structures-Less-than-200-Feet, Header 2: ALL applications are submitted online via MGO Connect",
        },
        {
          label: "Building Permit Expiration (months)",
          value: "12",
          codeSection: "Sec. 25.07.08. - Special exceptions.",
          source: "Rockville Municipal Code, Sec.25.07.08 and related provisions",
        },
        {
          label: "Building Permit Extension (months)",
          value: "12",
          codeSection: "n.Implementation period.",
          source: "Rockville, MD Code of Ordinances Sec. 25.07.08. - special exceptions.",
        },
        {
          label: "File Specifications and Naming Conventions",
          value: "",
          bullets: [
            'File Name: [HOUSE NUMBER] [STREET NAME] [FILE TYPE] - e.g., 123 Test St Checklist.pdf',
            "File Type: PDF only",
            "Page Sizes: minimum 11 x 17 inches",
            'Drawing Scale: minimum 1/4 inch = 1 foot (1/4\" = 1\'-0\")',
            "Drawing Quality: digitally created or scanned clearly, no dark backgrounds or blurred images",
            "Quantity of Plans: two or more sets for permits",
            "Upload Requirements: Combine multiple pages into one file",
            "File Upload: electronic submission through MyGovernmentOnline (MGO) Customer Portal",
          ],
          codeSection: "Various sections including Residential Plan Submittal Requirements Architectural header; Article I, Section 5-12 of Rockville City Code; DPS guidelines",
          source: "Rockville Residential Plan Submittal Requirements; Rockville Building Design Checklist and Other Permitting Documents; Montgomery County Plan Submittal Guidelines; IgCC Amendments for Rockville; Rockville City Code Section 5-12; Residential Plan Submittal Requirements Document; New Single-Family Dwelling or Townhouse Permit Documents",
        },
      ],
    },
    {
      id: "interconnection",
      number: "05",
      title: "Interconnection",
      icon: "plug",
      dataPoints: [
        {
          label: "Application Cost",
          value: "",
          bullets: [
            "Level 1: No fee",
            "Level 2: $50 plus $1 per kW inverter rating",
            "Level 3: $100 plus $2 per kW inverter rating",
            "Level 4: $100 plus $2 per kW inverter rating",
          ],
          codeSection: "Application Forms and Fees",
          source: "Pepco Maryland Interconnection Application & Agreement, Application Forms and Fees",
        },
        {
          label: "Application Level",
          value: "",
          bullets: [
            "Level 1: 10 kW or less",
            "Level 2: 2 MW or less",
            "Level 3: 50kW Area Network; 10MW Radial Distribution Circuit",
            "Level 4: up to 10 MW",
          ],
          codeSection: "Application Forms and Fees",
          source: "Pepco Holdings, Application Forms and Fees",
        },
        {
          label: "Excess Energy Export Credits",
          value: "$0.18354 per kWh (credit)",
          codeSection: 'SCHEDULE "PIV"',
          source: "PEPCO Rate Schedules for Electric Services in Maryland",
        },
      ],
    },
    {
      id: "municipal",
      number: "06",
      title: "Municipal Contact Information",
      icon: "landmark",
      dataPoints: [
        {
          label: "Building Department Address",
          value: "",
          bullets: [
            "Street Name and Number: 111 Maryland Ave.",
            "City: Rockville",
            "State: MD",
            "Zip Code: 20850",
            "Phone Number: 240-314-8240",
          ],
          codeSection: "Building Department Contact Information",
          source: "Montgomery County Department of Permitting Services documents",
        },
        {
          label: "Fire Department Phone",
          value: "(240) 314-8240",
          codeSection: "Inspection Services Division",
          source: "Fire-Protection-Systems-Inspection-Procedures, Inspection Services Division",
        },
        {
          label: "Permit Department Phone",
          value: "(240) 314-8240",
          codeSection: "Permits and Inspections Contact Information",
          source: "City of Rockville, Maryland, Community Planning and Development Services",
        },
        {
          label: "Building Department Hours of Operation",
          value: "",
          bullets: [
            "Monday: 8:30 a.m. - 5:00 p.m.",
            "Tuesday: 8:30 a.m. - 5:00 p.m.",
            "Wednesday: 8:30 a.m. - 5:00 p.m.",
            "Thursday: 8:30 a.m. - 5:00 p.m.",
            "Friday: 8:30 a.m. - 5:00 p.m.",
          ],
          codeSection: "Guidelines for Building Residential Decks - Section C; Express Permit Program _ City of Rockville.pdf sections 'Login During Express Permit Program Hours' and 'Select Express Permit Application'",
          source: "Monday & Thursday: Guidelines for Building Residential Decks, Section C. Where to Apply for Permits; Tuesday & Friday: Express Permit Program _ City of Rockville.pdf, sections 'Login During Express Permit Program Hours' and 'Select Express Permit Application'",
        },
        {
          label: "Building Department Official",
          value: "",
          bullets: [
            "Name: Evan Johnson",
            "Title: Director of Community Planning and Development Services",
            "Phone: 240-314-8200",
            "Email: cpds@rockvillemd.gov",
          ],
          source: "Rockville Building Department, Contact Information",
        },
        {
          label: "Electrical Inspection Phone",
          value: "(240) 314-8240",
          codeSection: "Inspection Services Division",
          source: "City of Rockville Inspection Services Division",
        },
        {
          label: "Inspection Department Officials",
          value: "",
          bullets: [
            "Angel Maldonado, Senior Codes Inspector",
            "James Metzgar, Senior Plans Examiner",
            "Volodumyr Borus, Codes Inspector",
            "Terrence Dickens, Senior Codes Inspector",
            "Paul Kern, Codes Inspector",
            "William Gressen, Senior Codes Inspector",
            "Jeremy McDougal, Codes Inspector",
            "Kesha Marshall, Codes Inspector",
            "Chris Dempwolf, Codes Inspection Manager",
          ],
          codeSection: "Article V, Sections 5-66 through 5-87; 2026-CPDS-Org-Chart-R022626",
          source: "Rockville City Code, Article V, Sections 5-66 through 5-87; 2026-CPDS-Org-Chart-R022626; Single-Family-Sign-Posting-Procedures-3-28-2025",
        },
        {
          label: "Planning Department Official",
          value: "",
          bullets: [
            "Name: Nicole Walters",
            "Title: Planner II",
            "Email: nwalters@rockvillemd.gov",
            "Phone: 240-314-8215",
          ],
          codeSection: "Contact",
          source: "Express Permit Program _ City of Rockville, Contact",
        },
      ],
    },
    {
      id: "hardware",
      number: "07",
      title: "Hardware Requirements",
      icon: "badge",
      dataPoints: [
        {
          label: "Energy Storage Requirements",
          value: "",
          bullets: [
            "UL Standard: 1741",
            "IEEE Standard: 1547-2018",
          ],
          codeSection: "IEEE Std 1547-2018 clauses 11.2.4.3, 11.2.5, 11.3; IEEE 1547.1-2020 Section 8",
          source: "PHI DER Technical Interconnection Requirements document, Sections 14.2 and J (COMAR 20.50.09)",
        },
        {
          label: "Solar Inverter Requirements",
          value: "",
          bullets: [
            "UL Standard 1741 SB",
            "IEEE Standard 1547-2018",
          ],
          codeSection: "Component-Specific Results from provided documents",
          source: "UL Standard 1741 - Pepco Acceptable Inverters documentation; IEEE Standard 1547 - GPC_AcceptableInvertersList_Pepco20160617.06.24.pdf; ETL Standard - Information not available in provided documents; Certifying Commission - Information not explicitly stated in the provided documents.",
        },
        {
          label: "Solar Module Requirements",
          value: "",
          bullets: [
            "UL Standard: UL 1741-SB, UL-1741-SC, UL-9741, UL-2594",
            "IEEE Standard: IEEE 1547-2018 and IEEE 1547a-2020",
            "Certifying Commission: California Energy Commission (CEC), California Public Utilities Commission (CPUC)",
          ],
          codeSection: "Acceptable Inverters and Inverter Standards sections",
          source: "Acceptable Inverters (Pepco - An Exelon Company.pdf); Inverter Standards (GPC_AcceptableInvertersList_Pepco20160617.06.24.pdf); PHI DER Technical Interconnection Requirements Appendix A",
        },
      ],
    },
  ],
  footnotes: [
    "Maryland Building Code - 2021, Section 2308.2.4 Basic Wind Speed",
    "StructuralDesignLoadMinimumRequiredDataList",
    "Maryland Code, Public Utilities, Sections (15) and (16)",
    "City of Rockville Zoning Code and Maryland State Solar Energy Regulations; City of Rockville Zoning Ordinance, Article 25.06.03; Article 25.09.05",
    "Maryland State Regulations on Solar Energy Generating Stations, Section on Setbacks for Solar Energy Generating Stations; City of Rockville Zoning Code, Section 25.12.04.a",
    "Maryland Public Utilities Article, Section 7-218(g)",
    "Maryland Public Service Commission Regulations, Section §7-218 (f) and (g)",
    "Rockville, MD Code of Ordinances, Sections 5-86, 5-286, 5-221, 5-101, 5-161, 5-321",
    "Sec. 5-131. - National Electrical Code-Adopted, Rockville, MD Code of Ordinances",
    "Ordinance-17-25-To-Amend-Chapter-9-of-the-Rockville-City-Code-to-Adopt-the-2024-Editions-of-the-Fire-Code-NFPA-1-and-the-Life-Safety-Code-NFPA-2024, Section 9-161",
    "Rockville Municipal Code, Sec.25.07.08 and related provisions",
    "Rockville, MD Code of Ordinances Sec. 25.07.08. - special exceptions.",
    "Rockville Residential Plan Submittal Requirements; Rockville Building Design Checklist and Other Permitting Documents; Montgomery County Plan Submittal Guidelines; IgCC Amendments for Rockville; Rockville City Code Section 5-12; Residential Plan Submittal Requirements Document; New Single-Family Dwelling or Townhouse Permit Documents",
    "Express-Accessory-Structure-Guide-Structures-Less-than-200-Feet, Header 2: ALL applications are submitted online via MGO Connect",
    "Pepco Maryland Interconnection Application & Agreement, Application Forms and Fees",
    "Pepco Holdings, Application Forms and Fees",
    "PEPCO Rate Schedules for Electric Services in Maryland",
    "Montgomery County Department of Permitting Services documents",
    "Fire-Protection-Systems-Inspection-Procedures, Inspection Services Division",
    "City of Rockville, Maryland, Community Planning and Development Services",
    "Monday & Thursday: Guidelines for Building Residential Decks, Section C. Where to Apply for Permits; Tuesday & Friday: Express Permit Program _ City of Rockville.pdf, sections 'Login During Express Permit Program Hours' and 'Select Express Permit Application'",
    "Rockville Building Department, Contact Information",
    "City of Rockville Inspection Services Division",
    "Rockville City Code, Article V, Sections 5-66 through 5-87; 2026-CPDS-Org-Chart-R022626; Single-Family-Sign-Posting-Procedures-3-28-2025",
    "Express Permit Program _ City of Rockville, Contact",
    "PHI DER Technical Interconnection Requirements document, Sections 14.2 and J (COMAR 20.50.09)",
    "UL Standard 1741 - Pepco Acceptable Inverters documentation; IEEE Standard 1547 - GPC_AcceptableInvertersList_Pepco20160617.06.24.pdf",
    "Acceptable Inverters (Pepco - An Exelon Company.pdf); Inverter Standards (GPC_AcceptableInvertersList_Pepco20160617.06.24.pdf); PHI DER Technical Interconnection Requirements Appendix A",
  ],
};

export const FREEDOM_POWER_REPORT: SampleReport = {
  ...CATALYZE_REPORT,
  slug: "freedom-power",
  reportId: "FP-6E2B9A47",
  prospect: "Freedom Power",
  prospectUrl: "https://www.freedompower.com/",
  reportRun: "Sep 2, 2026",
  verifiedLabel: "Sep 2026 · Seal of Authenticity",
};

export const ALTUS_POWER_REPORT: SampleReport = {
  ...PIVOT_REPORT,
  slug: "altus-power",
  reportId: "AP-8F3C6D24",
  prospect: "Altus Power",
  prospectUrl: "https://www.altuspower.com/",
  reportRun: "Sep 3, 2026",
};

export const CORE_DEVELOPMENT_GROUP_REPORT: SampleReport = {
  ...PIVOT_REPORT,
  slug: "core-development-group",
  reportId: "CD-9A3B7C12",
  prospect: "Core Development Group",
  prospectUrl: "https://www.coredevusa.com/",
  reportRun: "Sep 4, 2026",
};

export const VEROGY_REPORT: SampleReport = {
  ...PIVOT_REPORT,
  slug: "verogy",
  reportId: "VG-1A4B8C32",
  prospect: "Verogy",
  prospectUrl: "https://www.verogy.com/",
  reportRun: "Sep 4, 2026",
};

export const ENERFLO_REPORT: SampleReport = {
  slug: "enerflo",
  reportId: "EN-7C2D9A41",
  kicker: "Feasibility Report",
  title: "Rockville, MD (Residential)",
  prospect: "Enerflo",
  prospectUrl: "https://enerflo.io/",
  jurisdiction: "Rockville, MD",
  county: "Montgomery County",
  city: "Rockville",
  projectType: "Residential",
  systemSize: "8 kW DC",
  utility: "Pepco",
  reportRun: "Sep 21, 2026",
  verifiedLabel: "Sep 2026 · Seal of Authenticity",
  sections: [
    {
      id: "design",
      number: "01",
      title: "Design and Engineering",
      icon: "ruler",
      dataPoints: [
        {
          label: "Solar Rooftop Fire Access Pathways",
          value: "",
          bullets: [
            "Distance to ridge (arrays ≤33% roof area): 18 inches",
            "Distance to ridge (arrays >33% roof area): 36 inches with sprinklers up to 66% coverage; 36 inches over 66% coverage",
            "Eave to ridge path width: 36 inches per side (total 72 inches)",
            "Distance to skylights: 36 inches",
            "Distance to primary street-facing eave: 36 inches pathways (minimum two, on separate planes, one on street side)",
            "Distance to secondary street-facing eave: 18 inches up to 33% coverage, 36 inches over 33% coverage; 36 inches with sprinklers up to 66% coverage, 36 inches over 66% coverage",
          ],
          codeSection: "Sections 1205.2.1.1, 1205.2.1.2, 1205.2.1.3; R324.6.1; R324.6.2; R324.6.2.1; R324.6.3",
          source: "Rockville, MD Code of Ordinances, Chapter 9 - FIRE CODE; Sections 1205.2.1.1, 1205.2.1.2, 1205.2.1.3; IRC Sections R324.6.1, R324.6.2, R324.6.2.1, R324.6.3",
        },
        {
          label: "Solar Supply Side Connections Allowed",
          value: "Yes",
          codeSection: "Article 705.12",
          source: "NFPA-70-National-Electrical-Code-2023, Article 705.12",
        },
        {
          label: "Solar Supply Side Connections Allowed",
          value: "Yes",
          codeSection: "Sections 4.3 Battery Storage; 10.9.2 Sub-Station Power Transformers; Transformer requirements for DER >= 250 KVA",
          source: "PHI DER Technical Interconnection Requirements, Sections 4.3, 10.9.2, and relevant transformer requirements for DER >= 250 KVA",
        },
        {
          label: "Placard Requirements",
          value: "",
          bullets: [
            "\"Solar PV AC Disconnect\" placard, red color, on exterior AC disconnect",
            "\"Caution, Solar Electric System\" placard, yellow color, on meter housing",
            "Meter placard must indicate disconnect location if disconnect is away",
            "\"Rapid Shutdown Equipment\" placard with red border, white background",
            "\"PV Rapid Shutdown\" emergency responder placard",
            "Emergency Contact placard for battery systems",
            "Fire Safety Warning placard for energy storage batteries",
          ],
          codeSection: "Section FACILITY INFORMATION; Page 25",
          source: "Pepco_MD_Level_1_-_Interconnection_Application-Agreement_blank_2018Dec, Section FACILITY INFORMATION; Pepco_MD_Level_234_-_Interconnection_Application-Agreement_blank_2019April, Page 25",
        },
        {
          label: "Production Meter Required",
          value: "Conditional: Required for systems ≥ 25kW",
          codeSection: "Electric Service Handbook 2018 - Chapter 4, PHI DER Technical Interconnection Requirements, COMAR 20.50.10",
          source: "Electric Service Handbook 2018, Chapter 4 - Customers' Installations, Renewable Small Generation section; PHI DER Technical Interconnection Requirements; Code of Maryland Regulations 20.50.10 - Net Metering",
        },
        {
          label: "Production Meter Requirements",
          value: "",
          bullets: [
            "Production or REC Meter not required for systems under 25 kW",
            "Net-capable energy meter with reverse registration required",
            "Typically, only one production meter supplied and maintained by PEPCO",
            "Metering provided at no direct charge unless larger meter requested",
            "Line side wiring connects to top terminals of meter socket",
            "Load side wiring connects to bottom terminals of meter socket",
            "Isolating switch with visible break required on line side in some cases",
            "Approved Meter Socket Adapters allowed but do not replace net meter",
          ],
          codeSection: "Sections G and H under NET ENERGY METERING RIDER \"NEM\"; COMAR 20.50.10.01 and 20.50.10.02; COMAR 20.50.09.02, 20.50.09.08, 20.50.09.10; Section 13; Chapter 5 Section D",
          source: "2025-11-26_MASTER_MD_Pepco_Tariff_Book_pdf, NET ENERGY METERING RIDER \"NEM",
        },
        {
          label: "Solar AC Disconnecting Means Location",
          value: "The AC Disconnect must be in an accessible location outside or within 5 feet inside from service conductors entrance, not in bathrooms, within line of sight of service disconnect, inverter, and meter. If not in line of sight, placards indicating location are required.",
          codeSection: "Articles 690, 110.21(B), Section 1207.4.1; Rockville Code Chapter 5 Technical Standards Section 230.70; Sections 1205.4.1.2, 1205.4.3",
          source: "NFPA-70-National-Electrical-Code-2023 (NEC), Articles 690, 110.21(B) and Section 1207.4.1; Rockville, MD Code of Ordinances, Chapter 5 Technical Standards, Section 230.70; SECTION 1205 SOLAR PHOTOVOLTAIC POWER SYSTEMS, Sections 1205.4.1.2, 1205.4.3",
        },
        {
          label: "Solar AC Disconnecting Means Required",
          value: "Yes",
          codeSection: "690.13 Photovoltaic System Disconnecting Means",
          source: "NFPA-70-National-Electrical-Code-2023 (NEC), Section 690.13",
        },
        {
          label: "Solar AC Disconnecting Means Required",
          value: "Yes",
          codeSection: "PHI DER Technical Interconnection Requirements, Section 5.8",
          source: "PHI DER Technical Interconnection Requirements, Section 5.8 - Isolation Device; COMAR §20.50.09.06 - General Requirements",
        },
        {
          label: "BESS Heat Alarm Required",
          value: "Not Required if Sprinklers Exist",
          codeSection: "Section 1207.11.6 and Section 903.3.1.1",
          source: "Ordinance-17-25-To-Amend-Chapter-9-of-the-Rockville-City-Code-to-Adopt-the-2024-Editions-of-the-Fire-Code-NFPA-1-and-the-Life-Safety-Code-NFPA-2024, Section 1207.11.6 and Section 903.3.1.1",
        },
      ],
    },
    {
      id: "code-cycle",
      number: "02",
      title: "Code Cycle",
      icon: "book",
      dataPoints: [
        {
          label: "Building Code Edition",
          value: "2021 International Building Code",
          codeSection: "Sections 5-86, 5-286, 5-221, 5-101, 5-161, 5-321",
          source: "Rockville, MD Code of Ordinances, Sections 5-86, 5-286, 5-221, 5-101, 5-161, 5-321",
        },
        {
          label: "Electrical Code Edition",
          value: "2023 National Electrical Code",
          codeSection: "Sec. 5-131",
          source: "Sec. 5-131. - National Electrical Code-Adopted, Rockville, MD Code of Ordinances",
        },
        {
          label: "Fire Code Edition",
          value: "2024 NFPA 1 Fire Code",
          codeSection: "Section 9-161",
          source: "Ordinance-17-25-To-Amend-Chapter-9-of-the-Rockville-City-Code-to-Adopt-the-2024-Editions-of-the-Fire-Code-NFPA-1-and-the-Life-Safety-Code-NFPA-2024, Section 9-161",
        },
        {
          label: "Residential Code Edition",
          value: "2021 International Residential Code for One- and Two-Family Dwellings",
          codeSection: "Sec. 5-101",
          source: "Rockville, MD Code of Ordinances, Section 5-101",
        },
      ],
    },
    {
      id: "permitting",
      number: "03",
      title: "Permitting",
      icon: "clipboard",
      dataPoints: [
        {
          label: "Solar Expedited Permitting",
          value: "SolarAPP+",
          source: "Apply for an Automated Residential Solar Permit _ City of Rockville",
        },
        {
          label: "Solar Permit Cost",
          value: "$125",
          source: "Rockville MD FY27-ISD Fee Schedule",
        },
        {
          label: "Solar Professional Engineering Required",
          value: "Conditional: Required if array exceeds weight threshold, or if structural upgrades are performed to support PV loads.",
          codeSection: "Sections 5-86, 5-133; Residential-Building-Permit-Submittal-Requirements; Sections 5-66 through 5-87; Sections 1205.2.3, R905.17, R324.6; Section 1607.14.4.1",
          source: "Rockville, MD Code of Ordinances, Sections 5-86, 5-133; Residential-Building-Permit-Submittal-Requirements; City of Rockville Building Code, Sections 5-66 through 5-87; Rockville Local Building Code, Sections 1205.2.3, R905.17, R324.6; Residential Code, Section 1607.14.4.1",
        },
        {
          label: "Building Permit Expiration",
          value: "12",
          codeSection: "Sec. 25.07.08. - Special exceptions.",
          source: "Rockville Municipal Code, Sec.25.07.08 and related provisions",
        },
        {
          label: "Building Permit Extension",
          value: "12",
          codeSection: "n.Implementation period.",
          source: "Rockville, MD Code of Ordinances Sec. 25.07.08. - Special exceptions.",
        },
        {
          label: "Building Permit Submittal Method, Solar",
          value: "Online Portal",
          codeSection: "multiple sections",
          source: "Apply for an Automated Residential Solar Permit _ City of Rockville",
        },
        {
          label: "File Specifications and Naming Conventions",
          value: "",
          bullets: [
            "File Name: [HOUSE NUMBER] [STREET NAME] [FILE TYPE] - e.g., 123 Test St Checklist.pdf",
            "File Type: PDF only",
            "Page Sizes: minimum 11 x 17 inches",
            "Drawing Scale: minimum 1/4 inch = 1 foot (1/4\" = 1'-0\")",
            "Drawing Quality: digitally created or scanned clearly, no dark backgrounds or blurred images",
            "Quantity of Plans: two or more sets for permits",
            "Upload Requirements: Combine multiple pages into one file",
            "File Upload: electronic submission through MyGovernmentOnline (MGO) Customer Portal",
          ],
          codeSection: "Various sections including Residential Plan Submittal Requirements Architectural header; Article I, Section 5-12 of Rockville City Code; DPS guidelines",
          source: "Rockville Residential Plan Submittal Requirements; Rockville Building Design Checklist and Other Permitting Documents; Montgomery County Plan Submittal Guidelines; IgCC Amendments for Rockville; Rockville City Code Section 5-12; Residential Plan Submittal Requirements Document; New Single-Family Dwelling or Townhouse Permit Documents",
        },
        {
          label: "Permit Portal",
          value: "www.rockvillemd.gov/mgoconnect",
          codeSection: "Header 2: ALL applications are submitted online via MGO Connect",
          source: "Express-Accessory-Structure-Guide-Structures-Less-than-200-Feet, Header 2: ALL applications are submitted online via MGO Connect",
        },
        {
          label: "Solar Site Plan",
          value: "",
          bullets: [
            "Page Requirements:",
            "Drawn to scale",
            "North arrow",
            "Date",
            "Building/Site Identification:",
            "Setbacks to property lines",
            "Lot dimensions",
            "Dimensions and locations of drives, walks, fences, porches, patios, accessory buildings, planting areas, and freestanding signs",
            "Accurate location and dimensions of all improvements",
            "Solar Installation Details:",
            "Location of proposed solar structure, including roof arrays",
            "Firefighter access pathways, minimum 36 inches wide",
            "Ridge setbacks between 18 to 36 inches depending on array size",
            "Hardware Identification:",
            "Location of inverter",
            "Conduit routing pathways to electrical service panel",
            "Location of heat detectors for Battery Energy Storage Systems when applicable",
            "Architectural Details:",
            "Elevations, sections, and details as appropriate",
          ],
          codeSection: "Section 1205: Solar Photovoltaic Power Systems",
          source: "Residential Plan Submittal Requirements",
        },
        {
          label: "Solar Structural Plan",
          value: "",
          bullets: [
            "Calculations:",
            "Structural loads: snow, wind, design load criteria",
            "Unbalanced fill calculations",
            "Soil-bearing capacity in psf",
            "Compressive design strength for concrete elements",
            "Reinforcement steel placement details",
            "Truss design load calculations and bracing",
            "Structural Members:",
            "Structural columns and beams sizes and loads",
            "Joist and rafter sizes and spans",
            "Lateral blocking and framing details",
            "Bearing walls and collar ties",
            "Beam sizes with supporting calculations",
            "Building Sections and Cross-Sections:",
            "Dimensioned side view sections showing wall, floor, roof heights",
            "Footing depths and details",
            "Detailed roof cross-sections showing solar installation",
            "Solar Mounting Hardware:",
            "Dead load indications of PV panels and rack systems",
            "Solar rack attachments and connector details",
            "Truss connector types, locations, and permanent bracing",
            "Photovoltaic System Details:",
            "Roof plan view diagrams showing PV energized areas",
            "Permanent rapid shutdown and switch labels",
            "Distinctions for Roof Types:",
            "Stick-framed roofs: include lateral blocking and framing specifics",
            "Pre-engineered trussed roofs: provide sealed truss design drawings",
            "General Requirements:",
            "Plans drawn to scale and dimensioned",
            "Seal of licensed Structural Engineer or registered design professional",
          ],
          codeSection: "Structural; Sections 5-86 and 5-87; R802.10; R502.11.4; 1607.14.4; 1205.4.2; N104.1.2",
          source: "Residential-Building-Permit-Submittal-Requirements, Structural; City of Rockville Building Code, Sections 5-86 and 5-87; 2021 IRC, Sections R802.10, R502.11.4, 1607.14.4; SECTION 1205 SOLAR PHOTOVOLTAIC POWER SYSTEMS 1205.4.2; N104.1.2 Structural Plans, Specifications, and Engineering Details",
        },
        {
          label: "Solar Submittal Guidelines",
          value: "",
          bullets: [
            "Fully completed and signed Maryland Interconnection Application & Agreement",
            "Customer of record contact information",
            "Alternate contact information",
            "Facility information including address and Pepco account",
            "Type of application (new, addition, or upgrade)",
            "DC and AC inverter rating in kW",
            "Generator and inverter manufacturer name and model number",
            "Equipment installation contractor information",
            "Electrical contractor information",
            "Customer's signature",
            "Application fee (no fee for Level 1: 10 kW or less)",
            "Certificate of Completion",
            "Inspection Certificate with system type",
            "Single-line drawing of the system",
            "Site map",
          ],
          codeSection: "Level 1 Interconnection Application & Agreement Details, Parts I and II; Application Forms and Fees",
          source: "GPC_Pepco_MD_ApplicationChecklist_2015_01_28.pdf, Sections: Level 1 Interconnection Application & Agreement Details, Parts I and II, Application Forms and Fees",
        },
      ],
    },
    {
      id: "interconnection",
      number: "04",
      title: "Interconnection",
      icon: "plug",
      dataPoints: [
        {
          label: "Application Cost",
          value: "",
          bullets: [
            "Level 1: No fee",
            "Level 2: $50 plus $1 per kW inverter rating",
            "Level 3: $100 plus $2 per kW inverter rating",
            "Level 4: $100 plus $2 per kW inverter rating",
          ],
          codeSection: "Application Forms and Fees",
          source: "Pepco Maryland Interconnection Application & Agreement, Application Forms and Fees",
        },
        {
          label: "Application Level",
          value: "",
          bullets: [
            "Level 1: 10 kW or less",
            "Level 2: 2 MW or less",
            "Level 3: 50kW Area Network; 10MW Radial Distribution Circuit",
            "Level 4: up to 10 MW",
          ],
          codeSection: "Application Forms and Fees",
          source: "Pepco Holdings, Application Forms and Fees",
        },
        {
          label: "Excess Energy Export Credits",
          value: "$0.18354 per kWh (credit)",
          codeSection: "SCHEDULE \"PIV\"",
          source: "PEPCO Rate Schedules for Electric Services in Maryland",
        },
        {
          label: "Submittal Documents",
          value: "",
          bullets: [
            "Fully completed and signed Maryland Interconnection Application & Agreement",
            "Application fee (if applicable)",
            "Single-line drawing and site map",
            "Certificate of Completion",
            "Signed electric inspection form",
            "Interconnection customer contact information",
            "Existing utility account information",
            "Intended use of small generator facility",
            "Requested review level",
            "Technical info on generator and components",
            "Disconnect switch location and details",
            "Local or municipal electric code official approval",
          ],
          codeSection: "Section 20. Appendix F: Commissioning Checklist",
          source: "GPC Pepco Application Checklist; Pepco MD Level 1 - Interconnection Application-Agreement; PHI DER Technical Interconnection Requirements",
        },
      ],
    },
    {
      id: "licensing",
      number: "05",
      title: "Licensing",
      icon: "badge",
      dataPoints: [
        {
          label: "Solar Contractor Licensing",
          value: "",
          bullets: [
            "MHIC Contractor License",
            "Maryland State Contractor License",
            "Local Business License: No",
          ],
          codeSection: "Residential Solar Contractor Licensing Sections in City of Rockville and Montgomery County COMCOR 22.00.02.04",
          source: "City of Rockville Automated Residential Solar Permit documentation; Montgomery County COMCOR 22.00.02.04 Fire Protection System License",
        },
        {
          label: "Business License Portal",
          value: "https://www.dllr.state.md.us/license/elec/",
          source: "Residential Solar Panel Application",
        },
      ],
    },
    {
      id: "municipal",
      number: "06",
      title: "Municipal Contact Information",
      icon: "landmark",
      dataPoints: [
        {
          label: "Building Department Address",
          value: "",
          bullets: [
            "Street Name and Number: 111 Maryland Ave.",
            "City: Rockville",
            "State: MD",
            "Zip Code: 20850",
            "Phone Number: 240-314-8240",
          ],
          codeSection: "Building Department Contact Information",
          source: "Montgomery County Department of Permitting Services documents",
        },
        {
          label: "Building Department Hours of Operation",
          value: "",
          bullets: [
            "Monday: 8:30 a.m. - 5:00 p.m.",
            "Tuesday: 8:30 a.m. - 5:00 p.m.",
            "Wednesday: 8:30 a.m. - 5:00 p.m.",
            "Thursday: 8:30 a.m. - 5:00 p.m.",
            "Friday: 8:30 a.m. - 5:00 p.m.",
          ],
          codeSection: "Guidelines for Building Residential Decks - Section C; Express Permit Program _ City of Rockville.pdf sections 'Login During Express Permit Program Hours' and 'Select Express Permit Application'",
          source: "Monday & Thursday: Guidelines for Building Residential Decks, Section C. Where to Apply for Permits; Tuesday & Friday: Express Permit Program _ City of Rockville.pdf, sections 'Login During Express Permit Program Hours' and 'Select Express Permit Application'",
        },
        {
          label: "Building Department Official",
          value: "",
          bullets: [
            "Name: Evan Johnson",
            "Title: Director of Community Planning and Development Services",
            "Phone: 240-314-8200",
            "Email: cpds@rockvillemd.gov",
          ],
          source: "Rockville Building Department, Contact Information",
        },
        {
          label: "Electrical Inspection Phone",
          value: "(240) 314-8240",
          codeSection: "Inspection Services Division",
          source: "City of Rockville Inspection Services Division",
        },
        {
          label: "Fire Department Phone",
          value: "(240) 314-8240",
          codeSection: "Inspection Services Division",
          source: "Fire-Protection-Systems-Inspection-Procedures, Inspection Services Division",
        },
        {
          label: "Inspection Department Officials",
          value: "",
          bullets: [
            "Angel Maldonado, Senior Codes Inspector",
            "James Metzgar, Senior Plans Examiner",
            "Volodumyr Borus, Codes Inspector",
            "Terrence Dickens, Senior Codes Inspector",
            "Paul Kern, Codes Inspector",
            "William Gressen, Senior Codes Inspector",
            "Jeremy McDougal, Codes Inspector",
            "Kesha Marshall, Codes Inspector",
            "Chris Dempwolf, Codes Inspection Manager",
          ],
          codeSection: "Article V, Sections 5-66 through 5-87; 2026-CPDS-Org-Chart-R022626",
          source: "Rockville City Code, Article V, Sections 5-66 through 5-87; 2026-CPDS-Org-Chart-R022626; Single-Family-Sign-Posting-Procedures-3-28-2025",
        },
        {
          label: "Permit Department Phone",
          value: "(240) 314-8240",
          codeSection: "Permits and Inspections Contact Information",
          source: "City of Rockville, Maryland, Community Planning and Development Services",
        },
        {
          label: "Planning Department Official",
          value: "",
          bullets: [
            "Name: Nicole Walters",
            "Title: Planner II",
            "Email: nwalters@rockvillemd.gov",
            "Phone: 240-314-8215",
          ],
          codeSection: "Contact",
          source: "Express Permit Program _ City of Rockville, Contact",
        },
      ],
    },
    {
      id: "inspections",
      number: "07",
      title: "Inspections",
      icon: "search",
      dataPoints: [
        {
          label: "Electrical Inspection Method",
          value: "On-Site",
          codeSection: "Sections 19-7; 5-122; [A] 110 & [A] 109 Inspections",
          source: "Rockville, MD Code of Ordinances, Sections 19-7; 5-122; [A] 110 & [A] 109 Inspections",
        },
        {
          label: "Electrical Inspection Scheduling Method",
          value: "Online Portal",
          codeSection: "Online Scheduling Information; Fire-Protection-Systems-Inspection-Procedures",
          source: "City of Rockville Permit and Inspection Procedures, Online Scheduling Information; City of Rockville Inspection Procedures, Fire-Protection-Systems-Inspection-Procedures",
        },
        {
          label: "Inspection Portal",
          value: "www.rockvillemd.gov/mgoconnect",
          codeSection: "Header 2: ALL applications are submitted online via MGO Connect",
          source: "Express-Accessory-Structure-Guide-Structures-Less-than-200-Feet, Header 2: ALL applications are submitted online via MGO Connect",
        },
      ],
    },
  ],
  footnotes: [
    "Rockville, MD Code of Ordinances, Chapter 9 - FIRE CODE; Sections 1205.2.1.1, 1205.2.1.2, 1205.2.1.3; IRC Sections R324.6.1, R324.6.2, R324.6.2.1, R324.6.3",
    "NFPA-70-National-Electrical-Code-2023, Article 705.12",
    "PHI DER Technical Interconnection Requirements, Sections 4.3, 10.9.2, and relevant transformer requirements for DER >= 250 KVA",
    "Pepco_MD_Level_1_-_Interconnection_Application-Agreement_blank_2018Dec, Section FACILITY INFORMATION; Pepco_MD_Level_234_-_Interconnection_Application-Agreement_blank_2019April, Page 25",
    "Electric Service Handbook 2018, Chapter 4 - Customers' Installations, Renewable Small Generation section; PHI DER Technical Interconnection Requirements; Code of Maryland Regulations 20.50.10 - Net Metering",
    "2025-11-26_MASTER_MD_Pepco_Tariff_Book_pdf, NET ENERGY METERING RIDER \"NEM",
    "NFPA-70-National-Electrical-Code-2023 (NEC), Articles 690, 110.21(B) and Section 1207.4.1; Rockville, MD Code of Ordinances, Chapter 5 Technical Standards, Section 230.70; SECTION 1205 SOLAR PHOTOVOLTAIC POWER SYSTEMS, Sections 1205.4.1.2, 1205.4.3",
    "NFPA-70-National-Electrical-Code-2023 (NEC), Section 690.13",
    "PHI DER Technical Interconnection Requirements, Section 5.8 - Isolation Device; COMAR §20.50.09.06 - General Requirements",
    "Ordinance-17-25-To-Amend-Chapter-9-of-the-Rockville-City-Code-to-Adopt-the-2024-Editions-of-the-Fire-Code-NFPA-1-and-the-Life-Safety-Code-NFPA-2024, Section 1207.11.6 and Section 903.3.1.1",
    "Rockville, MD Code of Ordinances, Sections 5-86, 5-286, 5-221, 5-101, 5-161, 5-321",
    "Sec. 5-131. - National Electrical Code-Adopted, Rockville, MD Code of Ordinances",
    "Ordinance-17-25-To-Amend-Chapter-9-of-the-Rockville-City-Code-to-Adopt-the-2024-Editions-of-the-Fire-Code-NFPA-1-and-the-Life-Safety-Code-NFPA-2024, Section 9-161",
    "Rockville, MD Code of Ordinances, Section 5-101",
    "Apply for an Automated Residential Solar Permit _ City of Rockville",
    "Rockville MD FY27-ISD Fee Schedule",
    "Rockville, MD Code of Ordinances, Sections 5-86, 5-133; Residential-Building-Permit-Submittal-Requirements; City of Rockville Building Code, Sections 5-66 through 5-87; Rockville Local Building Code, Sections 1205.2.3, R905.17, R324.6; Residential Code, Section 1607.14.4.1",
    "Rockville Municipal Code, Sec.25.07.08 and related provisions",
    "Rockville, MD Code of Ordinances Sec. 25.07.08. - Special exceptions.",
    "Rockville Residential Plan Submittal Requirements; Rockville Building Design Checklist and Other Permitting Documents; Montgomery County Plan Submittal Guidelines; IgCC Amendments for Rockville; Rockville City Code Section 5-12; Residential Plan Submittal Requirements Document; New Single-Family Dwelling or Townhouse Permit Documents",
    "Express-Accessory-Structure-Guide-Structures-Less-than-200-Feet, Header 2: ALL applications are submitted online via MGO Connect",
    "Residential Plan Submittal Requirements",
    "Residential-Building-Permit-Submittal-Requirements, Structural; City of Rockville Building Code, Sections 5-86 and 5-87; 2021 IRC, Sections R802.10, R502.11.4, 1607.14.4; SECTION 1205 SOLAR PHOTOVOLTAIC POWER SYSTEMS 1205.4.2; N104.1.2 Structural Plans, Specifications, and Engineering Details",
    "GPC_Pepco_MD_ApplicationChecklist_2015_01_28.pdf, Sections: Level 1 Interconnection Application & Agreement Details, Parts I and II, Application Forms and Fees",
    "Pepco Maryland Interconnection Application & Agreement, Application Forms and Fees",
    "Pepco Holdings, Application Forms and Fees",
    "PEPCO Rate Schedules for Electric Services in Maryland",
    "GPC Pepco Application Checklist; Pepco MD Level 1 - Interconnection Application-Agreement; PHI DER Technical Interconnection Requirements",
    "City of Rockville Automated Residential Solar Permit documentation; Montgomery County COMCOR 22.00.02.04 Fire Protection System License",
    "Residential Solar Panel Application",
    "Montgomery County Department of Permitting Services documents",
    "Monday & Thursday: Guidelines for Building Residential Decks, Section C. Where to Apply for Permits; Tuesday & Friday: Express Permit Program _ City of Rockville.pdf, sections 'Login During Express Permit Program Hours' and 'Select Express Permit Application'",
    "Rockville Building Department, Contact Information",
    "City of Rockville Inspection Services Division",
    "Fire-Protection-Systems-Inspection-Procedures, Inspection Services Division",
    "Rockville City Code, Article V, Sections 5-66 through 5-87; 2026-CPDS-Org-Chart-R022626; Single-Family-Sign-Posting-Procedures-3-28-2025",
    "City of Rockville, Maryland, Community Planning and Development Services",
    "Express Permit Program _ City of Rockville, Contact",
    "Rockville, MD Code of Ordinances, Sections 19-7; 5-122; [A] 110 & [A] 109 Inspections",
    "City of Rockville Permit and Inspection Procedures, Online Scheduling Information; City of Rockville Inspection Procedures, Fire-Protection-Systems-Inspection-Procedures",
  ],
};
